import IOSInstallCTA from '../components/IOSInstallCTA';
import { PushReminderSlot } from '../components/PushPermissionSheet';
import CreationInvitationProgress from '../components/CreationInvitationProgress';
import { useRequestContext } from '../hooks/useRequestContext';
import PendingInvitationList from '../components/PendingInvitationList';
import { rosterPlayerLabel } from '../utils/rosterPlayerLabel';
import BulkInvitationResults, { bulkInvitationSummary } from '../components/BulkInvitationResults';
import { invitationPlayerLabel } from '../utils/invitationDisplay';
import { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { AuthContext, apiClient } from '../App';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Check, X, AlertTriangle, MessageCircle, Trash2, Send, Copy, Shield, UserPlus, Link2, Loader2, CheckCircle2, Clock, Share2, Pencil, XCircle, RefreshCw, Lock, Navigation, MapPin, Map, Settings, Users, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import Footer from '../components/Footer';
import { format, formatDistanceToNow } from 'date-fns';
import { fromUtcToLocalInput, toUtcIso, formatLocalDisplay, getUserTimezone, userTz, isPastRun } from '../utils/datetime';
import { deriveRunLifecycle, RUN_LIFECYCLE } from '../utils/runLifecycle';
import { canShowPlayerJoinAction } from '../utils/gameParticipation';
import { getConfirmationWindowState, CONFIRMATION_WINDOW } from '../utils/confirmationWindow';
import { tzDebugOn, getPreviewTz } from '../utils/previewTz';
import { formatAgeRange, formatAgeRangeSentence } from '../utils/ageRange';
import { beginPushPromptAction } from '../lib/pushPromptManager';
import AddressAutocomplete from '../components/AddressAutocomplete';
import { logJoinGame, logSwitchTeam } from '../utils/engagementTracker';
import GameTimePicker from '../components/GameTimePicker';
import { useUnsavedChangesGuard } from '../hooks/useUnsavedChangesGuard';
import { DiscardChangesDialog } from '../components/DiscardChangesDialog';
import PlayerFollowButton from '../components/PlayerFollowButton';
import PostGameFollowPrompt from '../components/PostGameFollowPrompt';
import PostGameFollowNudge from '../components/PostGameFollowNudge';
import ShareRunSheet from '../components/ShareRunSheet';
import { getParticipantCount } from '../utils/participantCount';
import PostJoinShareModal from '../components/PostJoinShareModal';
import HostFillBanner from '../components/HostFillBanner';
import QuickInviteDrawer from '../components/QuickInviteDrawer';
import DirectionsSheet from '../components/DirectionsSheet';
import ManageRunSheet from '../components/ManageRunSheet';
import { resolveAvatarUrl } from '../lib/avatarUrl';
import { formatVenue } from '../utils/venueDisplay';
import { getRunNoun } from '../utils/runNoun';
import {
  getDobRetryMessage,
  isDobRequiredError,
  PARTICIPATION_INTENT_TYPES,
  saveParticipationDobIntent,
} from '../lib/participationDob';

const FIELD_LABELS = {
  date_time: 'Time',
  title: 'Title',
  description: 'Description',
  address: 'Location',
  location_name: 'Venue',
  city: 'City',
  max_players: 'Spots',
  min_players: 'Min Players',
  location: 'Coordinates',
  tz: 'Timezone',
};

function formatChangeValue(field, value, tz) {
  if (value === null || value === undefined) return '';
  if (field === 'date_time') {
    return formatLocalDisplay(value, tz) || String(value);
  }
  if (field === 'location' && typeof value === 'object') {
    return 'Updated';
  }
  if (typeof value === 'object') {
    const oldNew = value?.old !== undefined ? value : null;
    if (oldNew) return String(oldNew);
    return JSON.stringify(value);
  }
  return String(value);
}

/**
 * Attendance Confidence chip styles.
 *
 * Backend (`get_game` enrichment) emits one of five labels:
 *   "Reliable" | "Usually shows" | "Mixed" | "Low confidence" | "New"
 *
 * All chips use the same low-saturation palette — no harsh reds or greens —
 * so the chip reads as informational, never punitive. Raw percentages are
 * intentionally hidden per product spec.
 */
const CONFIDENCE_CHIP_CLASSES = {
  'Reliable': 'bg-emerald-50 text-emerald-700',
  'Usually shows': 'bg-sky-50 text-sky-700',
  'Mixed': 'bg-amber-50 text-amber-700',
  'Low confidence': 'bg-zinc-100 text-zinc-600',
  'New': 'bg-zinc-100 text-zinc-500',
};
const confidenceChipClass = (label) =>
  CONFIDENCE_CHIP_CLASSES[label] || 'bg-zinc-100 text-zinc-500';

// Outcome capture is intentionally hidden for the pickup-run experience.
// Keep the submission implementation intact so a future league/tournament mode
// can re-enable it without changing backend fields, APIs, or stored data.
const SHOW_GAME_OUTCOME_SUBMISSION_UI = false;

const GameDetail = () => {
  const { id } = useParams();
  const { user, token } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [directionsOpen, setDirectionsOpen] = useState(false);
  const [manageRunOpen, setManageRunOpen] = useState(false);
  const [editForm, setEditForm] = useState({});
  const editFormInitialRef = useRef(null);
  const [audits, setAudits] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isLeaving, setIsLeaving] = useState(false);
  const [locInput, setLocInput] = useState('');
  const [selectedPlace, setSelectedPlace] = useState(null);

  // Unsaved-changes guard for the Edit Run modal. Back/swipe closes the modal
  // first (stays on Run Detail); prompts only when the form was actually edited.
  const isEditDirty = isEditModalOpen && editFormInitialRef.current != null &&
    JSON.stringify(editForm) !== editFormInitialRef.current;
  const {
    promptOpen: editPromptOpen,
    confirmLeave: editConfirmLeave,
    cancelLeave: editCancelLeave,
    guardedLeave: editGuardedLeave,
  } = useUnsavedChangesGuard({
    active: isEditModalOpen,
    shouldPrompt: isEditDirty,
    onLeave: () => setIsEditModalOpen(false),
  });
  
  // Validation: Check if selected edit date_time is in the past
  const isEditDateTimeInPast = editForm.date_time_input && new Date(editForm.date_time_input) <= new Date();

  // Max players validation — balanced-team product rule: even integers 2–98.
  const editMaxPlayersError = (() => {
    const v = editForm.max_players;
    if (v === '' || v === null || v === undefined || Number.isNaN(Number(v))) return 'Enter a number of players';
    const n = Number(v);
    if (!Number.isInteger(n)) return 'Whole numbers only';
    if (n < 2) return 'Minimum is 2 players';
    if (n > 98) return 'Maximum is 98 players';
    if (n % 2 !== 0) return 'Must be an even number (teams stay balanced)';
    return null;
  })();
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [lastSubmitDebug, setLastSubmitDebug] = useState(null);
  const [showAdminCancelModal, setShowAdminCancelModal] = useState(false);
  const [adminCancelReason, setAdminCancelReason] = useState('');
  
  // Outcome submission state
  const [outcomeStep, setOutcomeStep] = useState('attendance'); // 'attendance' | 'winner' | 'confirm'
  const [teamAParticipated, setTeamAParticipated] = useState(null);
  const [teamBParticipated, setTeamBParticipated] = useState(null);
  const [winnerTeam, setWinnerTeam] = useState(null);
  const [isSubmittingOutcome, setIsSubmittingOutcome] = useState(false);
  
  // Comments state
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [isPostingComment, setIsPostingComment] = useState(false);
  const [loadingComments, setLoadingComments] = useState(false);
  const [waitlistStatus, setWaitlistStatus] = useState({
    checked: false,
    userOnWaitlist: false,
    userPosition: null,
  });
  const [joiningWaitlist, setJoiningWaitlist] = useState(false);

  useEffect(() => {
    if (!location.state?.dobCompletionSuccess) return;
    toast.success(getDobRetryMessage(location.state.participationIntentType));
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  const requestParticipationDob = (type) => {
    const saved = saveParticipationDobIntent({ type, returnTo: `/games/${id}` });
    if (!saved) {
      toast.error('Unable to open date of birth entry. Please try again.');
      return false;
    }
    navigate('/complete-dob');
    return true;
  };
  
  // Follow state for roster players - tracks both following AND requested states
  const [followStatusMap, setFollowStatusMap] = useState({});
  // followStatusMap = { [userId]: { following: bool, requested: bool } }
  const [showPostGamePrompt, setShowPostGamePrompt] = useState(true);
  const [showFollowNudge, setShowFollowNudge] = useState(false);
  
  // Confirmation state for roster players
  const [confirmationMap, setConfirmationMap] = useState({});
  // confirmationMap = { [userId]: { status: 'confirmed'|'pending'|..., confirmed_at: string|null } }
  const [confirmationSummary, setConfirmationSummary] = useState({ confirmed: 0, total: 0 });
  // Backend-authoritative confirmation window {state, opens_at, closes_at} from roster-status.
  const [confirmationWindow, setConfirmationWindow] = useState(null);
  
  // Invite system state
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [invitablePlayers, setInvitablePlayers] = useState([]);
  const [loadingInvitablePlayers, setLoadingInvitablePlayers] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [invitingPlayer, setInvitingPlayer] = useState(null);
  const [coHostInput, setCoHostInput] = useState('');
  const [addingCoHost, setAddingCoHost] = useState(false);
  const [removingCoHostId, setRemovingCoHostId] = useState(null);
  const [coHostSuggestions, setCoHostSuggestions] = useState([]);
  const [loadingCoHostSuggestions, setLoadingCoHostSuggestions] = useState(false);

  // Club fan-out invite state (host-only block at top of the invite modal)
  const [hostClubs, setHostClubs] = useState([]);
  const [loadingHostClubs, setLoadingHostClubs] = useState(false);
  const [invitingClubId, setInvitingClubId] = useState(null);
  const [invitedClubIds, setInvitedClubIds] = useState(new Set());
  const [confirmClub, setConfirmClub] = useState(null);
  const [clubInviteResults, setClubInviteResults] = useState([]);
  const requests = useRequestContext(JSON.stringify([id, user?.id || null]));
  const [editClubOptions, setEditClubOptions] = useState([]);
  const [loadingEditClubOptions, setLoadingEditClubOptions] = useState(false);

  // Share system state
  const [shareSheetOpen, setShareSheetOpen] = useState(false);
  const [postJoinModalOpen, setPostJoinModalOpen] = useState(false);

  // Invited-players visibility (host-only collapsible list).
  const [invitedExpanded, setInvitedExpanded] = useState(false);
  
  // Squad suggestions state (prefetched for instant post-join drawer)
  const [squadSuggestions, setSquadSuggestions] = useState([]);
  const [quickInviteOpen, setQuickInviteOpen] = useState(false);
  const [invitedSquadUsers, setInvitedSquadUsers] = useState(new Set());

  useEffect(() => {
    // Parallel initial fetch — game, audits, comments all fire at once
    setGame(null);
    setInstallAfterJoin(null);
    setLoading(true);
    Promise.all([fetchGame(), fetchAudits(), fetchComments()]);
    // Fetch follow status after game loads (need player IDs first)
  }, [id, user?.id]);

  useEffect(() => {
    setInvitingClubId(null);
    setInvitedClubIds(new Set());
    setConfirmClub(null);
    setInviteModalOpen(false);
    setHostClubs([]);
    setInvitablePlayers([]);
    setLoadingHostClubs(false);
    setLoadingInvitablePlayers(false);
    setAddingCoHost(false);
    setRemovingCoHostId(null);
    setClubInviteResults([]);
  }, [id, user?.id]);
  
  // Prefetch squad suggestions when we have both game ID and token
  useEffect(() => {
    if (token && id) {
      prefetchSquadSuggestions();
    }
  }, [token, id]);
  
  // Fetch follow + confirmation status in parallel when game data arrives
  useEffect(() => {
    if (user && game) {
      Promise.all([fetchFollowingStatus(), fetchConfirmationStatus()]);
    }
  }, [user?.id, game?.id]);

  useEffect(() => {
    if (user && game?.id && game.waitlist_enabled !== false) {
      fetchWaitlistStatus();
    } else {
      setWaitlistStatus({ checked: false, userOnWaitlist: false, userPosition: null });
    }
  }, [user?.id, game?.id, game?.waitlist_enabled]);

  useEffect(() => {
    if (!isEditModalOpen || !game?.id || !user?.id || game.creator_id !== user.id) {
      setCoHostSuggestions([]);
      return;
    }
    fetchCoHostSuggestions(editForm.run_club_ids || []);
  }, [isEditModalOpen, game?.id, game?.creator_id, user?.id, JSON.stringify(editForm.run_club_ids || []), game?.co_host_ids?.join(',')]);

  const normalizedRunClubIds = (run) => {
    const multi = Array.isArray(run?.run_club_ids)
      ? run.run_club_ids.map((id) => String(id || '').trim()).filter(Boolean)
      : [];
    if (multi.length > 0) return [...new Set(multi)];
    const legacy = String(run?.run_club_id || '').trim();
    return legacy ? [legacy] : [];
  };

  const canPostToClub = (club) => (
    club?.capabilities?.can_create_club_run === true ||
    club?.is_owner === true ||
    club?.is_manager === true
  );

  const toggleEditClub = (clubId) => {
    setEditForm((prev) => {
      const selected = Array.isArray(prev.run_club_ids) ? prev.run_club_ids : [];
      const next = selected.includes(clubId)
        ? selected.filter((id) => id !== clubId)
        : [...selected, clubId];
      return { ...prev, run_club_ids: next };
    });
  };

  const fetchEditableClubs = async () => {
    if (!user || !game || game.creator_id !== user.id) return;
    setLoadingEditClubOptions(true);
    try {
      const response = await apiClient.get('/clubs/mine');
      setEditClubOptions((response.data?.clubs || []).filter(canPostToClub));
    } catch {
      setEditClubOptions([]);
    } finally {
      setLoadingEditClubOptions(false);
    }
  };

  const fetchCoHostSuggestions = async (selectedClubIds = []) => {
    if (!game?.id) return;
    setLoadingCoHostSuggestions(true);
    try {
      const params = new URLSearchParams();
      if (selectedClubIds.length > 0) {
        selectedClubIds.forEach((clubId) => params.append('club_ids', clubId));
      } else {
        params.append('club_ids', '');
      }
      const response = await apiClient.get(`/games/${game.id}/co-host-suggestions?${params.toString()}`);
      setCoHostSuggestions(response.data?.suggestions || []);
    } catch {
      setCoHostSuggestions([]);
    } finally {
      setLoadingCoHostSuggestions(false);
    }
  };
  
  // Fetch which players the current user follows AND has pending requests for
  const fetchFollowingStatus = async () => {
    if (!user || !game) return;
    
    // Get all unique player IDs from both teams
    const allPlayers = [...(game.team_a_players || []), ...(game.team_b_players || [])];
    const playerIds = [...new Set(allPlayers.map(p => p.user_id).filter(id => id && id !== user.id))];
    
    if (playerIds.length === 0) return;
    
    try {
      // Use bulk status endpoint to get both following and requested states
      const res = await apiClient.get(`/follow/status-bulk?user_ids=${playerIds.join(',')}`);
      setFollowStatusMap(res.data.statuses || {});
    } catch (error) {
      console.error('Failed to fetch follow status:', error);
      // Fallback to empty
      setFollowStatusMap({});
    }
  };
  
  // Handle follow state change from PlayerFollowButton
  const handleFollowChange = (userId, newState) => {
    // newState = { following: bool, requested: bool }
    setFollowStatusMap(prev => ({
      ...prev,
      [userId]: newState
    }));
  };

  // Fetch confirmation status for all roster players
  const fetchConfirmationStatus = async () => {
    if (!game) return;
    try {
      const res = await apiClient.get(`/games/${game.id}/roster-status`);
      setConfirmationMap(res.data.players || {});
      setConfirmationSummary({
        confirmed: res.data.confirmed_count || 0,
        total: res.data.total_count || 0,
      });
      setConfirmationWindow(res.data.confirmation_window || null);
    } catch {
      // Silently fail — confirmation display is non-blocking
      setConfirmationMap({});
    }
  };
  
  // Prefetch squad suggestions for instant post-join drawer
  const prefetchSquadSuggestions = async () => {
    if (!token) {
      return;
    }
    try {
      const response = await apiClient.get(`/games/${id}/squad-suggestions`);
      if (response.data?.suggestions) {
        setSquadSuggestions(response.data.suggestions);
      }
    } catch (err) {
      // Silent fail - this is just a prefetch optimization
    }
  };
  
  // Track when a squad invite is sent
  const handleSquadInviteSent = (userId) => {
    setInvitedSquadUsers(prev => new Set([...prev, userId]));
  };

  // Confirm current user's spot
  const handleConfirmSpot = async () => {
    setIsConfirming(true);
    try {
      await apiClient.post(`/games/${game.id}/confirm`);
      // Update local state immediately
      setConfirmationMap(prev => ({
        ...prev,
        [user.id]: { status: 'confirmed', confirmed_at: new Date().toISOString() }
      }));
      setConfirmationSummary(prev => ({
        ...prev,
        confirmed: prev.confirmed + 1,
      }));
      toast.success('Spot confirmed!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm');
      // Refetch on failure so stale UI (e.g. a stale CANCELLED/expired record
      // that still rendered the button) self-corrects and the button hides.
      fetchConfirmationStatus();
    } finally {
      setIsConfirming(false);
    }
  };

  // Format time: "6:30 PM"
  const formatTimeOnly = (dateStr, tz) => {
    if (!dateStr) return '';
    try {
      // Backend now sends UTC with +00:00 suffix via middleware
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit', 
        hour12: true,
        timeZone: tz || undefined 
      });
    } catch {
      return '';
    }
  };

  // Format day abbreviation: "Mon"
  const formatDayAbbr = (dateStr, tz) => {
    if (!dateStr) return '';
    try {
      // Backend now sends UTC with +00:00 suffix via middleware
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleDateString('en-US', { weekday: 'short', timeZone: tz || undefined });
    } catch {
      return '';
    }
  };

  // Format date: "Mar 2"
  const formatDateShort = (dateStr, tz) => {
    if (!dateStr) return '';
    try {
      // Backend now sends UTC with +00:00 suffix via middleware
      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: tz || undefined });
    } catch {
      return '';
    }
  };
  const fetchGame = async ({ invitationRefresh = false } = {}) => {
    const isCurrent = requests.begin('session');
    if (!isCurrent()) return;
    try {
      const response = await apiClient.get(`/games/${id}`);
      if (isCurrent()) setGame(response.data);
    } catch (error) {
      if (isCurrent()) {
        if (invitationRefresh) toast.warning('Invitations processed. Session details could not refresh; your results remain below.');
        else toast.error('Failed to fetch game details');
      }
    } finally {
      if (isCurrent()) setLoading(false);
    }
  };

  const fetchWaitlistStatus = async () => {
    try {
      const response = await apiClient.get(`/games/${id}/waitlist`);
      setWaitlistStatus({
        checked: true,
        userOnWaitlist: !!response.data?.user_on_waitlist,
        userPosition: response.data?.user_position || null,
      });
    } catch (error) {
      setWaitlistStatus({ checked: true, userOnWaitlist: false, userPosition: null });
    }
  };

  const fetchAudits = async () => {
    try {
      const response = await apiClient.get(`/games/${id}/audits`);
      setAudits(response.data.audits || []);
    } catch (error) {
      console.error('Failed to fetch audits:', error);
      console.error('Failed to fetch audits:', error);
    }
  };

  // Comments functions
  const fetchComments = async () => {
    setLoadingComments(true);
    try {
      const response = await apiClient.get(`/games/${id}/comments`);
      setComments(response.data.comments || []);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
      console.error('Failed to fetch comments:', error);
    } finally {
      setLoadingComments(false);
    }
  };

  const postComment = async () => {
    if (!newComment.trim()) return;
    
    setIsPostingComment(true);
    try {
      await apiClient.post(`/games/${id}/comments`, { text: newComment.trim() });
      setNewComment('');
      fetchComments(); // Refresh comments
      toast.success('Comment posted');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to post comment';
      toast.error(msg);
    } finally {
      setIsPostingComment(false);
    }
  };

  const deleteComment = async (commentId) => {
    try {
      await apiClient.delete(`/games/${id}/comments/${commentId}`);
      fetchComments(); // Refresh comments
      toast.success('Comment deleted');
    } catch (error) {
      toast.error('Failed to delete comment');
    }
  };

  const handleEditClick = () => {
    // Pre-fill form with current game values
    const tz = game.tz || getUserTimezone();
    const localInput = fromUtcToLocalInput(game.date_time, tz);
    const selectedClubIds = normalizedRunClubIds(game);
    
    // Debug logging
    console.table({
      tag: 'edit.open',
      utc: game.date_time,
      tz: tz,
      localInput: localInput,
    });
    
    setEditForm({
      title: game.title || '',
      description: game.description || '',
      public_share_message: game.public_share_message || '',
      date_time_input: localInput,
      address: game.address || '',
      city: game.city || '',
      zip_code: game.zip_code || '',
      privacy: game.privacy || 'public',
      is_private: game.is_private === true || game.privacy === 'private',
      skill_level_min: game.skill_level_min || 'Unranked',
      skill_level_max: game.skill_level_max || 'Elite',
      age_min: game.age_min || 13,
      age_max: game.age_max || 99,
      max_players: game.max_players || 10,
      min_players: game.min_players || 4,
      court_type: game.court_type || 'full_court',
      run_club_ids: selectedClubIds,
      tz: tz
    });
    // Snapshot for unsaved-changes detection inside the edit modal.
    editFormInitialRef.current = JSON.stringify({
      title: game.title || '',
      description: game.description || '',
      public_share_message: game.public_share_message || '',
      date_time_input: localInput,
      address: game.address || '',
      city: game.city || '',
      zip_code: game.zip_code || '',
      privacy: game.privacy || 'public',
      is_private: game.is_private === true || game.privacy === 'private',
      skill_level_min: game.skill_level_min || 'Unranked',
      skill_level_max: game.skill_level_max || 'Elite',
      age_min: game.age_min || 13,
      age_max: game.age_max || 99,
      max_players: game.max_players || 10,
      min_players: game.min_players || 4,
      court_type: game.court_type || 'full_court',
      run_club_ids: selectedClubIds,
      tz: tz
    });
    
    // Initialize location state separately
    setLocInput(game.location_name || '');
    setSelectedPlace(
      game.place_id
        ? {
            label: game.location_name,
            place_id: game.place_id,
            lat: game.latitude,
            lng: game.longitude
          }
        : null
    );
    
    setIsEditModalOpen(true);
    fetchEditableClubs();
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    // Frontend validation: Cannot schedule in the past
    if (editForm.date_time_input) {
      const selectedDateTime = new Date(editForm.date_time_input);
      const now = new Date();
      if (selectedDateTime <= now) {
        toast.error("Game time must be in the future");
        return;
      }
    }
    
    setIsSaving(true);
    
    try {
      // Convert local input to UTC
      const utcOut = toUtcIso(editForm.date_time_input, editForm.tz);
      
      // Debug logging
      const submitDebug = {
        tag: 'edit.submit',
        localInput: editForm.date_time_input,
        tz: editForm.tz,
        utcOut: utcOut,
      };
      setLastSubmitDebug(submitDebug);
      
      // Build payload with only changed fields
      const payload = {
        title: editForm.title,
        description: editForm.description,
        public_share_message: editForm.public_share_message || '',
        date_time: utcOut,
        location_name: locInput,
        address: editForm.address,
        city: editForm.city,
        zip_code: editForm.zip_code,
        privacy: editForm.privacy,
        is_private: editForm.is_private,
        skill_level_min: editForm.skill_level_min,
        skill_level_max: editForm.skill_level_max,
        court_type: editForm.court_type,
        tz: editForm.tz
      };

      if (game?.creator_id === user?.id) {
        const selectedClubIds = Array.isArray(editForm.run_club_ids) ? editForm.run_club_ids : [];
        payload.run_club_ids = selectedClubIds;
        payload.run_club_id = selectedClubIds[0] || null;
      }
      
      // Coerce numbers
      ['age_min', 'age_max', 'min_players', 'max_players'].forEach(k => {
        if (editForm[k] !== undefined && editForm[k] !== '') {
          payload[k] = parseInt(editForm[k], 10);
        }
      });
      
      // Include place data if location was updated
      if (selectedPlace) {
        payload.place_id = selectedPlace.place_id;
        payload.latitude = selectedPlace.lat;
        payload.longitude = selectedPlace.lng;
      } else if (game.place_id) {
        // Keep existing place data if not changed
        payload.place_id = game.place_id;
        payload.latitude = game.latitude;
        payload.longitude = game.longitude;
      }
      
      const response = await apiClient.put(`/games/${id}/edit`, payload);
      toast.success(response.data.message || 'Game updated successfully!');
      setIsEditModalOpen(false);
      
      // Refetch game and audits to sync UI
      await Promise.all([fetchGame(), fetchAudits()]);
    } catch (error) {
      console.error('[Edit Game] Error:', error);
      console.error('[Edit Game] Error:', error);
      console.error('[Edit Game] Error response:', error?.response?.data);
      console.error('[Edit Game] Error response:', error?.response?.data);
      
      // Extract detailed error message
      let msg = 'Failed to update game';
      if (error?.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          msg = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          // Pydantic validation errors
          msg = error.response.data.detail.map(err => err.msg).join(', ');
        }
      }
      
      toast.error(msg);
    } finally {
      setIsSaving(false);
    }
  };

  const [joiningTeam, setJoiningTeam] = useState(null);
  const [installAfterJoin, setInstallAfterJoin] = useState(null);
  useEffect(() => {
    const intent = location.state?.iosInstallAfterJoin;
    if (intent?.id !== id || intent?.userId !== user?.id) return;
    setInstallAfterJoin(intent);
    // Consume the preview's one-time success signal without changing the route.
    const { iosInstallAfterJoin, ...state } = location.state;
    navigate(`${location.pathname}${location.search}${location.hash}`, { replace: true, state });
  }, [id, user?.id, location.state, location.pathname, location.search, location.hash, navigate]);

  /**
   * Open Pickup join: posts to team_a first. If team_a is full (409), retries
   * team_b. This treats team_a/team_b as internal storage bins behind a single
   * "Join Run" UX. Same downstream behavior (engagement event, share modal,
   * follow nudge) regardless of which bucket the user lands in.
   */
  const handleJoinRun = async () => {
    if (!user?.date_of_birth) {
      requestParticipationDob(PARTICIPATION_INTENT_TYPES.DIRECT_JOIN);
      return;
    }
    const finishPromptAction = beginPushPromptAction();
    setJoiningTeam('any');
    let landedTeam = null;
    let lastError = null;
    try {
      // Try team_a first
      try {
        const response = await apiClient.post(`/games/${id}/join/team_a`);
        landedTeam = 'team_a';
        if (response.data?.idempotent) {
          toast.info("You're already in this run");
        } else {
          setInstallAfterJoin({ id, userId: user.id });
          toast.success('Successfully joined!');
        }
      } catch (err) {
        // If team_a is full (409), try team_b
        if (err?.response?.status === 409) {
          try {
            const response = await apiClient.post(`/games/${id}/join/team_b`);
            landedTeam = 'team_b';
            if (response.data?.idempotent) {
              toast.info("You're already in this run");
            } else {
              setInstallAfterJoin({ id, userId: user.id });
              toast.success('Successfully joined!');
            }
          } catch (err2) {
            lastError = err2;
          }
        } else {
          lastError = err;
        }
      }

      if (!landedTeam) {
        if (isDobRequiredError(lastError)) {
          requestParticipationDob(PARTICIPATION_INTENT_TYPES.DIRECT_JOIN);
          return;
        }
        const msg = lastError?.response?.data?.detail || lastError?.message || 'Failed to join run';
        toast.error(msg);
        console.error('Join run error:', lastError);
        return;
      }

      // Post-join engagement: log event, show quick-invite drawer or share modal, follow nudge
      const teamLetter = landedTeam === 'team_a' ? 'A' : 'B';
      logJoinGame(id, teamLetter);

      const inviteKey = `quick_invite_shown_${id}`;
      if (!sessionStorage.getItem(inviteKey) && squadSuggestions.length > 0) {
        setQuickInviteOpen(true);
        sessionStorage.setItem(inviteKey, 'true');
      } else {
        const shareKey = `post_join_share_shown_${id}`;
        if (!sessionStorage.getItem(shareKey)) {
          setTimeout(() => setPostJoinModalOpen(true), 500);
          sessionStorage.setItem(shareKey, 'true');
        }
      }

      const nudgeKey = `follow_nudge_shown_${id}`;
      if (!sessionStorage.getItem(nudgeKey)) {
        setShowFollowNudge(true);
        sessionStorage.setItem(nudgeKey, 'true');
        setTimeout(() => setShowFollowNudge(false), 10000);
      }

      // High-intent push permission prompt — only fires inside Capacitor iOS
      // shell and only when state machine allows. Never blocks join flow.
      try { finishPromptAction.joined(); } catch { /* noop */ }

      fetchGame();
    } finally {
      finishPromptAction();
      setJoiningTeam(null);
    }
  };

  const handleJoinWaitlist = async () => {
    if (!user?.date_of_birth) {
      requestParticipationDob(PARTICIPATION_INTENT_TYPES.WAITLIST_JOIN);
      return;
    }
    setJoiningWaitlist(true);
    try {
      const response = await apiClient.post(`/games/${id}/waitlist/join`);
      setWaitlistStatus({
        checked: true,
        userOnWaitlist: true,
        userPosition: response.data?.position || response.data?.waitlist_count || null,
      });
      toast.success("You're on the waitlist");
      fetchGame();
    } catch (error) {
      if (isDobRequiredError(error)) {
        requestParticipationDob(PARTICIPATION_INTENT_TYPES.WAITLIST_JOIN);
        return;
      }
      const detail = error.response?.data?.detail || 'Failed to join waitlist';
      if (String(detail).toLowerCase().includes('already on the waitlist')) {
        setWaitlistStatus({ checked: true, userOnWaitlist: true, userPosition: null });
        toast.info("You're already on the waitlist");
      } else {
        toast.error(detail);
      }
    } finally {
      setJoiningWaitlist(false);
    }
  };

  const handleCancelGame = async () => {
    setIsCancelling(true);
    try {
      await apiClient.put(`/games/${id}/status`, {
        status: 'cancelled',
        cancel_reason: cancelReason || undefined
      });
      toast.success('Game cancelled successfully');
      setShowCancelModal(false);
      setCancelReason('');
      fetchGame();
      fetchAudits();
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Failed to cancel game';
      toast.error(msg);
    } finally {
      setIsCancelling(false);
    }
  };

  // Admin cancel game handler
  const handleAdminCancelGame = async () => {
    try {
      await apiClient.post(`/admin/games/${id}/cancel`, null, {
        params: { reason: adminCancelReason || 'Cancelled by admin' }
      });
      toast.success('Game cancelled by admin');
      setShowAdminCancelModal(false);
      setAdminCancelReason('');
      fetchGame();
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Failed to cancel game';
      toast.error(msg);
    }
  };

  const handleReopenGame = async () => {
    try {
      await apiClient.put(`/games/${id}/status`, {
        status: 'open'
      });
      toast.success('Game reopened successfully');
      fetchGame();
      fetchAudits();
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Failed to reopen game';
      toast.error(msg);
    }
  };

  // === Invite System Functions ===
  
  const openInviteModal = async () => {
    if (!game) return;
    const isCurrent = requests.begin('picker');
    if (!isCurrent()) return;
    setInviteModalOpen(true);
    setLoadingInvitablePlayers(true);
    setSearchQuery('');

    // Fetch the host's clubs for the fan-out "Invite a Club" block. Best-effort:
    // a failure here must not block the per-player invite flow. Block is hidden
    // entirely when the host has zero clubs.
    setLoadingHostClubs(true);
    apiClient.get('/clubs/mine')
      .then((res) => { if (isCurrent()) setHostClubs(res.data.clubs || []); })
      .catch(() => { if (isCurrent()) setHostClubs([]); })
      .finally(() => { if (isCurrent()) setLoadingHostClubs(false); });

    try {
      const response = await apiClient.get(`/games/${game.id}/invitable-players`);
      if (!isCurrent()) return;
      setInvitablePlayers(response.data.players || []);
    } catch (error) {
      if (!isCurrent()) return;
      toast.error('Failed to load invitable players');
      setInvitablePlayers([]);
    } finally {
      if (isCurrent()) setLoadingInvitablePlayers(false);
    }
  };

  // Eligibility and per-member outcomes are decided by the backend.
  const handleInviteClub = async (club) => {
    if (!game || !club) return;
    const isCurrentContext = requests.begin('bulk');
    if (!isCurrentContext()) return;
    setConfirmClub(null);
    setInvitingClubId(club.id);
    try {
      const response = await apiClient.post(`/games/${game.id}/invite-club`, {
        club_id: club.id,
      });
      const data = response.data || {};
      if (!isCurrentContext()) return;
      setClubInviteResults((previous) => [...previous, {
        gameId: game.id, viewerId: user?.id, clubName: club.name, data,
      }]);
      const summary = bulkInvitationSummary(data);
      if (summary.failed || summary.warnings) toast.warning(summary.text);
      else toast.success(summary.text);
      setInvitedClubIds((prev) => new Set([...prev, club.id]));
      // Shared sequencing prevents any older session read from replacing this one.
      await fetchGame({ invitationRefresh: true });
    } catch (error) {
      if (isCurrentContext()) toast.error(error.response?.data?.detail || 'Failed to invite club');
    } finally {
      if (isCurrentContext()) setInvitingClubId(null);
    }
  };

  const handleInvitePlayer = async (playerId) => {
    if (!game) return;
    setInvitingPlayer(playerId);
    try {
      const response = await apiClient.post(`/games/${game.id}/invite`, {
        invited_user_id: playerId
      });
      
      if (response.data.already_invited) {
        toast.info('Player already invited');
      } else {
        toast.success('Invite sent!');
      }
      
      // Update local state to mark as invited
      setInvitablePlayers(prev => 
        prev.map(p => p.id === playerId ? { ...p, already_invited: true } : p)
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send invite');
    } finally {
      setInvitingPlayer(null);
    }
  };

  const addCoHostByIdentifier = async (identifierValue) => {
    const identifier = String(identifierValue || '').trim();
    if (!game || !identifier) return;
    const isCurrent = requests.begin('cohost-add');
    if (!isCurrent()) return;
    const isLatestSession = requests.begin('session');
    setAddingCoHost(true);
    try {
      const response = await apiClient.post(`/games/${game.id}/co-hosts`, { identifier });
      if (!isCurrent()) return;
      if (isLatestSession()) setGame(prev => ({
        ...prev,
        co_hosts: response.data?.co_hosts || [],
        co_host_ids: response.data?.co_host_ids || [],
      }));
      setCoHostInput('');
      toast.success(response.data?.already_cohost ? 'Already a co-host' : 'Co-host added');
      const addedId = response.data?.co_host?.user_id;
      if (addedId) {
        setCoHostSuggestions((prev) => prev.filter((suggestion) => suggestion.user_id !== addedId));
      }
    } catch (error) {
      if (isCurrent()) toast.error(error.response?.data?.detail || 'Failed to add co-host');
    } finally {
      if (isCurrent()) setAddingCoHost(false);
    }
  };

  const handleAddCoHost = async () => {
    await addCoHostByIdentifier(coHostInput);
  };

  const handleRemoveCoHost = async (coHostId) => {
    if (!game || !coHostId) return;
    const isCurrent = requests.begin('cohost-remove');
    if (!isCurrent()) return;
    const isLatestSession = requests.begin('session');
    setRemovingCoHostId(coHostId);
    try {
      const response = await apiClient.delete(`/games/${game.id}/co-hosts/${coHostId}`);
      if (!isCurrent()) return;
      if (isLatestSession()) setGame(prev => ({
        ...prev,
        co_hosts: response.data?.co_hosts || [],
        co_host_ids: response.data?.co_host_ids || [],
      }));
      toast.success('Co-host removed');
    } catch (error) {
      if (isCurrent()) toast.error(error.response?.data?.detail || 'Failed to remove co-host');
    } finally {
      if (isCurrent()) setRemovingCoHostId(null);
    }
  };

  const renderCoHostManagement = () => (
    <div className="rounded-lg border border-zinc-200 bg-zinc-50 p-4" data-testid="cohost-management">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-zinc-900">Co-hosts</p>
          <p className="mt-1 text-xs leading-5 text-zinc-600">
            Co-hosts can help manage this run but cannot cancel/delete it or manage co-hosts.
          </p>
        </div>
        {coHosts.length > 0 && (
          <span className="shrink-0 rounded-full bg-white px-2 py-0.5 text-xs font-medium text-zinc-500 border border-zinc-200">
            {coHosts.length}
          </span>
        )}
      </div>

      {coHosts.length > 0 ? (
        <div className="mt-3 space-y-2">
          {coHosts.map((coHost) => (
            <div
              key={coHost.user_id}
              className="flex items-center justify-between gap-3 rounded-md bg-white px-3 py-2 border border-zinc-200"
            >
              <span className="min-w-0 truncate text-sm font-medium text-zinc-800">
                {coHost.display_name || coHost.username}
              </span>
              <button
                type="button"
                onClick={() => handleRemoveCoHost(coHost.user_id)}
                disabled={removingCoHostId === coHost.user_id}
                className="w-8 h-8 inline-flex items-center justify-center rounded-full text-zinc-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-50"
                aria-label={`Remove ${coHost.display_name || coHost.username}`}
                data-testid={`remove-cohost-${coHost.user_id}`}
              >
                {removingCoHostId === coHost.user_id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <X className="w-4 h-4" />
                )}
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="mt-3 text-sm text-zinc-500">No co-hosts added yet.</p>
      )}

      {coHostSuggestions.length > 0 && (
        <div className="mt-4 rounded-md border border-orange-100 bg-white p-3" data-testid="cohost-suggestions">
          <p className="text-sm font-semibold text-zinc-900">Suggested co-hosts</p>
          <p className="mt-0.5 text-xs text-zinc-500">
            People you often host with or connected club managers.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {coHostSuggestions.map((suggestion) => (
              <button
                key={suggestion.user_id}
                type="button"
                onClick={() => addCoHostByIdentifier(suggestion.user_id)}
                disabled={addingCoHost}
                className="inline-flex max-w-full items-center gap-2 rounded-full border border-zinc-200 bg-zinc-50 px-3 py-1.5 text-xs font-medium text-zinc-700 hover:border-orange-200 hover:bg-orange-50 hover:text-orange-700 disabled:opacity-50"
                data-testid={`cohost-suggestion-${suggestion.user_id}`}
              >
                <span className="truncate">{suggestion.display_name || suggestion.username}</span>
                <UserPlus className="h-3.5 w-3.5 shrink-0" />
              </button>
            ))}
          </div>
        </div>
      )}
      {loadingCoHostSuggestions && (
        <p className="mt-3 text-xs text-zinc-500">Loading co-host suggestions...</p>
      )}

      <div className="mt-3 flex gap-2">
        <Input
          value={coHostInput}
          onChange={(e) => setCoHostInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAddCoHost();
            }
          }}
          placeholder="Username or email"
          className="h-10 bg-white"
          data-testid="cohost-input"
        />
        <Button
          type="button"
          size="sm"
          onClick={handleAddCoHost}
          disabled={!coHostInput.trim() || addingCoHost}
          className="h-10 shrink-0 bg-orange-500 hover:bg-orange-600 text-white"
          data-testid="add-cohost-btn"
        >
          {addingCoHost ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );

  const copyInviteLink = () => {
    if (!game) return;
    
    const link = `${window.location.origin}/join/${game.id}`;
    navigator.clipboard.writeText(link)
      .then(() => toast.success('Invite link copied!'))
      .catch(() => toast.error('Failed to copy link'));
  };
  
  const filteredInvitablePlayers = invitablePlayers.filter(player => 
    invitationPlayerLabel(player).toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleLeaveGame = async () => {
    setIsLeaving(true);
    try {
      await apiClient.post(`/games/${id}/leave`);
      toast.success('Left the game');
      fetchGame();
    } catch (error) {
      toast.error('Failed to leave game');
    } finally {
      setIsLeaving(false);
    }
  };

  const getSkillBadgeClass = (skill) => {
    const skillLower = skill?.toLowerCase();
    if (skillLower === 'beginner') return 'skill-badge skill-beginner';
    if (skillLower === 'intermediate') return 'skill-badge skill-intermediate';
    if (skillLower === 'advanced') return 'skill-badge skill-advanced';
    if (skillLower === 'pro') return 'skill-badge skill-pro';
    return 'skill-badge skill-intermediate';
  };

  const teamA = game?.team_a_players || [];
  const teamB = game?.team_b_players || [];
  const isInTeamA = teamA.some(p => p.user_id === user?.id);
  const isInTeamB = teamB.some(p => p.user_id === user?.id);
  const isJoined = isInTeamA || isInTeamB;
  const isCreator = game?.creator_id === user?.id;
  const coHostIds = game?.co_host_ids || [];
  const coHosts = game?.co_hosts || [];
  const isCoHost = !!user?.id && coHostIds.some(id => String(id) === String(user.id));
  const canUseHostTools = isCreator || isCoHost;
  // Admin support-view: a strict admin who is neither the host nor a rostered
  // participant. Player/social mutation surfaces are suppressed for this viewer
  // (read-only support context). Deliberate, labeled admin controls (e.g.
  // "Cancel This Game (Admin)") are intentionally NOT suppressed here.
  const isSupportViewer = !!user && user.role === 'admin' && !canUseHostTools && !isJoined;
  // Noun source of truth: centralized getRunNoun (existing court_type field).
  const runNoun = getRunNoun(game);
  const runClubs = Array.isArray(game?.run_clubs) && game.run_clubs.length > 0
    ? game.run_clubs
    : (game?.run_club ? [game.run_club] : []);
  const primaryRunClub = runClubs[0];
  const otherRunClubCount = Math.max(0, runClubs.length - 1);
  const maxPlayers = game?.max_players || 10;
  const totalPlayers = getParticipantCount(game);
  // Effective capacity accounts for backend's per-team cap (max_players // 2).
  // For even max_players (the only values the Create form allows), this equals
  // max_players (no-op). For legacy odd max_players runs, this rounds down to
  // the actual fillable total — preventing the UI from showing
  // "14/15 going · 1 spot open" when both internal teams are actually full.
  const effectiveMaxPlayers = Math.floor(maxPlayers / 2) * 2;
  const isRunFull = totalPlayers >= effectiveMaxPlayers;
  const isWaitlistEnabled = game?.waitlist_enabled !== false;
  const isWaitlisted = waitlistStatus.userOnWaitlist;
  const canJoinWaitlist = isRunFull && isWaitlistEnabled && !isWaitlisted;

  // Viewer-independent run lifecycle (cancelled > completed > awaiting_outcome > active).
  // Join/waitlist UI only renders for ACTIVE runs; past runs surface an
  // awaiting-outcome state (host can submit the outcome).
  const lifecycle = deriveRunLifecycle(game || {});
  const isActiveRun = lifecycle === RUN_LIFECYCLE.ACTIVE;
  const isAwaitingOutcome = lifecycle === RUN_LIFECYCLE.AWAITING_OUTCOME;

  // Pre-lock-in roster UX:
  // While the run is below its minimum player threshold, emphasize viability
  // ("X joined · N more needed") and render subtle placeholder rows up to
  // min_players — not max_players — so the run feels close to happening
  // rather than mostly empty. Falls back to existing capacity language
  // ("X/Y going · Z spots open") the moment min_players is reached.
  // `min_players || 0` keeps legacy MongoDB docs without the field harmless.
  const minPlayers = game?.min_players || 0;
  const isPreLockIn =
    minPlayers > 0 && totalPlayers < minPlayers && totalPlayers < effectiveMaxPlayers;
  const stillNeeded = Math.max(0, minPlayers - totalPlayers);
  const placeholdersShow = isPreLockIn
    ? Math.min(stillNeeded, effectiveMaxPlayers - totalPlayers)
    : 0;
  
  // Age eligibility check
  const userAge = Number.isFinite(user?.age) ? user.age : null;
  const gameAgeMin = game?.age_min || 0;
  const gameAgeMax = game?.age_max || 99;
  const isAgeEligible = userAge !== null && userAge >= gameAgeMin && userAge <= gameAgeMax;
  const isAgeIneligible = userAge !== null && !isAgeEligible && !isCreator; // Co-hosts follow normal player eligibility.
  const isAgeRestricted = isAgeIneligible && !isJoined; // Display logic: show banner/hide roster only for non-joined ineligible users
  const isMinor = userAge !== null && userAge < 18;
  const isAdultOnlyRun = gameAgeMin >= 18;
  const showPlayerJoinAction = canShowPlayerJoinAction({
    isActiveRun,
    isPrimaryHost: isCreator,
    isJoined,
    isAgeIneligible,
  });
  
  // Open Pickup: render one unified roster.
  // team_a_players / team_b_players are kept as internal storage buckets only —
  // the backend still has per-team capacity (max_players // 2) which we handle
  // in handleJoinRun via the team_a → team_b 409 fallback. Post-game outcome
  // UI below (host attendance + winner submission) still references the two
  // teams separately and is intentionally left unchanged.
  const sortedRoster = useMemo(() => {
    const combined = [...teamA, ...teamB];
    // Confirmed players first, then unconfirmed, preserving join order within each group
    return combined.sort((a, b) => {
      const aConf = confirmationMap[a.user_id]?.status === 'confirmed' ? 0 : 1;
      const bConf = confirmationMap[b.user_id]?.status === 'confirmed' ? 0 : 1;
      return aConf - bConf;
    });
  }, [teamA, teamB, confirmationMap]);

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-50 pb-24" data-testid="game-detail-skeleton">
        <div className="px-4 pt-4 pb-6 max-w-lg mx-auto animate-pulse">
          {/* Back button */}
          <div className="h-5 bg-zinc-200 rounded w-16 mb-4" />

          {/* Hero card */}
          <div className="bg-white rounded-xl border border-zinc-200 px-5 py-5 mb-4">
            <div className="h-4 bg-zinc-200 rounded w-2/5 mb-3" />
            <div className="h-7 bg-zinc-200 rounded w-4/5 mb-2" />
            <div className="h-4 bg-zinc-200 rounded w-3/5 mb-4" />
            <div className="flex gap-2">
              <div className="h-7 bg-zinc-200 rounded-full w-20" />
              <div className="h-7 bg-zinc-200 rounded-full w-24" />
              <div className="h-7 bg-zinc-200 rounded-full w-28" />
            </div>
          </div>

          {/* CTA button */}
          <div className="h-12 bg-zinc-200 rounded-xl w-full mb-6" />

          {/* Team A card */}
          <div className="bg-white rounded-xl border border-zinc-200 p-4 mb-4">
            <div className="h-4 bg-zinc-200 rounded w-24 mb-4" />
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-zinc-200 rounded-full" />
                <div className="flex-1">
                  <div className="h-4 bg-zinc-200 rounded w-28 mb-1" />
                  <div className="h-3 bg-zinc-200 rounded w-20" />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-zinc-200 rounded-full" />
                <div className="flex-1">
                  <div className="h-4 bg-zinc-200 rounded w-32 mb-1" />
                  <div className="h-3 bg-zinc-200 rounded w-16" />
                </div>
              </div>
            </div>
          </div>

          {/* Team B card */}
          <div className="bg-white rounded-xl border border-zinc-200 p-4 mb-4">
            <div className="h-4 bg-zinc-200 rounded w-24 mb-4" />
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-zinc-200 rounded-full" />
                <div className="flex-1">
                  <div className="h-4 bg-zinc-200 rounded w-24 mb-1" />
                  <div className="h-3 bg-zinc-200 rounded w-20" />
                </div>
              </div>
            </div>
          </div>

          {/* Chat card */}
          <div className="bg-white rounded-xl border border-zinc-200 p-4">
            <div className="h-4 bg-zinc-200 rounded w-16 mb-4" />
            <div className="h-10 bg-zinc-100 rounded-2xl w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-orange-100 flex items-center justify-center">
        <p className="text-gray-600">Game not found</p>
      </div>
    );
  }

  // Debug logging for display rendering
  if (tzDebugOn()) {
    console.table({
      tag: 'detail.render',
      utc: game.date_time,
      tz: game.tz || getUserTimezone(),
      display: formatLocalDisplay(game.date_time, game.tz || getUserTimezone())
    });
  }

  return (
    <div className="min-h-screen bg-zinc-50 pb-24">
      {/* Debug Banner - Preview Only */}
      {tzDebugOn() && game && (
        <div className="fixed top-4 right-4 bg-yellow-100 border-2 border-yellow-400 p-3 rounded-lg shadow-lg z-50 max-w-md text-xs font-mono">
          <div className="font-bold mb-2 text-yellow-800">TZ Debug (Preview Only)</div>
          <div className="space-y-1 text-yellow-900">
            <div><strong>Browser TZ:</strong> {getUserTimezone()}</div>
            <div><strong>Preview Override:</strong> {getPreviewTz() || 'None'}</div>
            <div><strong>Game TZ:</strong> {game.tz || 'Not set'}</div>
            <div><strong>Game UTC:</strong> {game.date_time}</div>
            <div><strong>Display:</strong> {formatLocalDisplay(game.date_time, game.tz || getUserTimezone())}</div>
            <div><strong>Edit Prefill:</strong> {fromUtcToLocalInput(game.date_time, game.tz || getUserTimezone())}</div>
            {lastSubmitDebug && (
              <>
                <hr className="my-2 border-yellow-400" />
                <div className="text-yellow-800 font-semibold">Last Submit:</div>
                <div><strong>Local Input:</strong> {lastSubmitDebug.localInput}</div>
                <div><strong>TZ:</strong> {lastSubmitDebug.tz}</div>
                <div><strong>UTC Out:</strong> {lastSubmitDebug.utcOut}</div>
              </>
            )}
          </div>
        </div>
      )}
      
      <div className="px-4 pt-4 pb-6 max-w-lg mx-auto">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => {
            // History-aware back: return to the actual previous page (e.g. Admin
            // Host detail, feed, notifications, club). React Router tracks the
            // history position in window.history.state.idx; idx > 0 means there
            // is a real in-app entry to pop. Otherwise (direct/deep-link open
            // with no prior history) fall back to a safe default.
            if (window.history.state?.idx > 0) {
              navigate(-1);
            } else {
              navigate('/dashboard');
            }
          }}
          className="mobile-page-back mb-4 h-9 px-0 text-sm font-medium text-zinc-500 hover:text-zinc-900 hover:bg-transparent"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <PushReminderSlot blocked={!!joiningTeam || postJoinModalOpen || quickInviteOpen} />
        {installAfterJoin?.id === id && installAfterJoin?.userId === user?.id &&
          <IOSInstallCTA authenticated={Boolean(user)} placement="post-join" />}

        {/* === HERO RUN HEADER === */}
        <div className="bg-white rounded-xl border border-zinc-200 shadow-[0_1px_2px_rgba(0,0,0,0.04)] px-5 py-5 mb-4">
          {/* Date/Time line */}
          <p className="text-sm font-semibold text-zinc-500 uppercase tracking-wide mb-2">
            {formatDayAbbr(game.date_time, game.tz || getUserTimezone())} • {formatTimeOnly(game.date_time, game.tz || getUserTimezone())} — {formatDateShort(game.date_time, game.tz || getUserTimezone())}
          </p>

          {/* Run Title - Largest element + Manage utility */}
          <div className="flex items-start justify-between gap-3 mb-1">
            <h1 className="text-2xl font-bold text-zinc-900 leading-tight" data-testid="game-title">
              {game.title}
            </h1>
            {(canUseHostTools || (user && ['admin', 'scout'].includes(user.role))) &&
              game.status !== 'completed' && game.status !== 'cancelled' && (
              <button
                type="button"
                onClick={() => setManageRunOpen(true)}
                className="shrink-0 -mr-1 -mt-1 w-9 h-9 inline-flex items-center justify-center rounded-full text-zinc-500 hover:text-zinc-800 hover:bg-zinc-100 active:bg-zinc-200 active:scale-[0.95] transition-all"
                aria-label={`Manage ${runNoun}`}
                data-testid="manage-run-btn"
              >
                <Settings className="w-5 h-5" />
              </button>
            )}
          </div>
          
          {/* Venue — honest name/address display (see utils/venueDisplay) */}
          {(() => {
            const { primary, secondary } = formatVenue(game.location_name, game.address);
            return (
              <div className="mb-2" data-testid="venue-display">
                <p className="text-[15px] text-zinc-700 font-medium" data-testid="venue-primary">{primary}</p>
                {secondary && (
                  <p className="text-[13px] text-zinc-500" data-testid="venue-secondary">{secondary}</p>
                )}
              </div>
            );
          })()}

          {/* Get Directions */}
          {(() => {
            const lat = game.latitude;
            const lng = game.longitude;
            const hasLocation =
              (typeof lat === 'number' && typeof lng === 'number') ||
              !!game.address ||
              !!game.location_name ||
              !!game.city;
            if (!hasLocation) {
              return (
                <p
                  className="inline-flex items-center gap-1.5 text-xs text-zinc-400 mb-4"
                  data-testid="get-directions-unavailable"
                >
                  <Navigation className="w-3.5 h-3.5" />
                  Location unavailable
                </p>
              );
            }
            return (
              <button
                type="button"
                onClick={() => setDirectionsOpen(true)}
                data-testid="get-directions-btn"
                className="inline-flex items-center gap-2 mb-4 px-4 py-2 rounded-full text-sm font-semibold text-orange-700 bg-orange-50 hover:bg-orange-100 active:bg-orange-200 active:scale-[0.98] transition-all"
              >
                <Navigation className="w-4 h-4" />
                Get Directions
              </button>
            );
          })()}

          {/* Metadata Chips */}
          <div className="flex flex-wrap gap-2">
            {game.court_type === 'training' && (
              <span
                className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-orange-100 text-orange-700"
                data-testid="training-badge"
              >
                Training
              </span>
            )}
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-zinc-100 text-zinc-700">
              {game.skill_level_min === game.skill_level_max 
                ? game.skill_level_min 
                : `${game.skill_level_min} – ${game.skill_level_max}`}
            </span>
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-zinc-100 text-zinc-700">
              {formatAgeRange(game.age_min, game.age_max)}
            </span>
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-zinc-100 text-zinc-700">
              {totalPlayers}/{effectiveMaxPlayers} players
            </span>
            {game.city && (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-zinc-100 text-zinc-700">
                {game.city}
              </span>
            )}
          </div>

          {/* Host attribution — credits the organizer for every run, including
              organizer-only hosts (host_is_participant=false) who are not on the
              roster and therefore don't count toward capacity. */}
          {game.host?.display_name && (
            <button
              type="button"
              onClick={() => game.host?.username && navigate(`/player/${game.host.username}`)}
              className="flex items-center gap-2 mt-3 group"
              data-testid="game-host-attribution"
            >
              <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-medium relative flex-shrink-0 overflow-hidden bg-orange-50 text-orange-700 ring-1 ring-orange-200" data-testid="game-host-avatar">
                <span>{game.host.display_name?.charAt(0).toUpperCase() || 'H'}</span>
                {game.host.profile_image_url && (() => {
                  const raw = game.host.profile_image_url;
                  const src = raw?.startsWith?.('http')
                    ? raw
                    : `${process.env.REACT_APP_BACKEND_URL || ''}${raw}`;
                  return (
                    <img
                      src={src}
                      alt=""
                      loading="lazy"
                      onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      className="absolute inset-0 w-full h-full object-cover rounded-full"
                      data-testid="game-host-avatar-img"
                    />
                  );
                })()}
              </div>
              <span className="text-[13px] text-zinc-600">
                Hosted by{' '}
                <span className="font-semibold text-zinc-900 group-hover:text-orange-600 transition-colors" data-testid="game-host-name">
                  {game.host.display_name}
                </span>
              </span>
            </button>
          )}

          {coHosts.length > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[13px] text-zinc-600" data-testid="game-cohosts">
              <span className="font-medium text-zinc-700">Co-hosts:</span>
              {coHosts.map((coHost, index) => (
                <span key={coHost.user_id} className="font-semibold text-zinc-900">
                  {coHost.display_name || coHost.username}
                  {index < coHosts.length - 1 ? ',' : ''}
                </span>
              ))}
            </div>
          )}

          {/* Primary Actions - Anchored to Hero Card */}
          {canUseHostTools && game.status !== 'completed' && game.status !== 'cancelled' && (
            <div className="flex gap-3 mt-5">
              <Button
                onClick={openInviteModal}
                className="flex-1 h-12 rounded-xl font-semibold bg-orange-500 hover:bg-orange-600 text-white gap-2 transition-transform duration-100 active:scale-[0.98] active:brightness-95"
                data-testid="invite-players-btn"
              >
                <UserPlus className="w-4 h-4" />
                Invite Players
              </Button>
              <Button
                onClick={() => setShareSheetOpen(true)}
                className="flex-1 h-12 rounded-xl font-semibold bg-orange-500 hover:bg-orange-600 text-white gap-2 transition-transform duration-100 active:scale-[0.98] active:brightness-95"
                data-testid="share-run-btn"
              >
                <Share2 className="w-4 h-4" />
                Share {runNoun}
              </Button>
            </div>
          )}
        </div>

        {/* Show cancel reason if present */}
        {game.status === 'cancelled' && game.cancel_reason && (
          <div className="mb-6 p-4 bg-red-50 rounded-xl border border-red-100">
            <p className="text-sm text-red-700">
              <span className="font-semibold">Cancelled:</span> {game.cancel_reason}
            </p>
          </div>
        )}
        
        {/* Age Restriction Banner for ineligible users */}
        {isAgeRestricted && (
          <div className="mb-6 p-4 bg-zinc-100 rounded-xl border border-zinc-200" data-testid="age-restriction-banner">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-zinc-200 flex items-center justify-center flex-shrink-0">
                <Lock className="w-5 h-5 text-zinc-500" />
              </div>
              <div>
                <p className="text-sm font-semibold text-zinc-800">Age Restricted</p>
                <p className="text-sm text-zinc-600 mt-0.5">
                  This {runNoun.toLowerCase()} is for {formatAgeRangeSentence(gameAgeMin, gameAgeMax)}. You're not eligible to join.
                </p>
              </div>
            </div>
          </div>
        )}

        {game.description && (
          <p className="text-sm text-zinc-600 mb-6 leading-relaxed">{game.description}</p>
        )}

        {/* Posted-to-Club pill — honest visibility indicator.
            Renders only when this run is linked to a club via run_club_id.
            Wording is deliberately "Posted to" (not "invited") because no
            notifications are fanned out to club members in v1. */}
        {primaryRunClub && primaryRunClub.slug && (
          <button
            type="button"
            onClick={() => navigate(`/club/${primaryRunClub.slug}`)}
            className="inline-flex items-center gap-2 mb-4 px-3 py-1.5 rounded-full bg-zinc-50 border border-zinc-200 text-xs text-zinc-700 hover:bg-zinc-100 transition-colors active:scale-[0.98]"
            data-testid="posted-to-club-pill"
            aria-label={`Posted to ${primaryRunClub.name}${otherRunClubCount > 0 ? ` and ${otherRunClubCount} other club${otherRunClubCount === 1 ? '' : 's'}` : ''}. Tap to open club.`}
          >
            <Users className="w-3.5 h-3.5 text-zinc-500" />
            <span>
              Posted to{' '}
              <span className="font-semibold text-zinc-900">{primaryRunClub.name}</span>
              {otherRunClubCount > 0 ? (
                <span className="text-zinc-500"> + {otherRunClubCount} other club{otherRunClubCount === 1 ? '' : 's'}</span>
              ) : typeof primaryRunClub.member_count === 'number' && (
                <span className="text-zinc-500"> · {primaryRunClub.member_count} member{primaryRunClub.member_count === 1 ? '' : 's'}</span>
              )}
            </span>
          </button>
        )}

        {canUseHostTools && <CreationInvitationProgress gameId={game.id} />}

        {/* STATUS - Full notification (active runs only) */}
        {isActiveRun && totalPlayers >= maxPlayers && (
          <p className="text-sm text-zinc-600 mb-4">This {runNoun.toLowerCase()} is full.</p>
        )}

        {/* Awaiting-outcome state — run has ended, no outcome submitted yet. */}
        {SHOW_GAME_OUTCOME_SUBMISSION_UI && isAwaitingOutcome && (
          <div className="mb-6 p-4 bg-amber-50 rounded-xl border border-amber-100" data-testid="awaiting-outcome-banner">
            <p className="text-sm font-semibold text-amber-800">This {runNoun.toLowerCase()} has ended</p>
            <p className="text-sm text-amber-700 mt-0.5">
              {isCreator ? 'Submit the game outcome below.' : "Awaiting the host's game outcome."}
            </p>
          </div>
        )}

        {/* JOIN SECTION — single "Join Run" for Open Pickup (active runs only) */}
        {/* Age-ineligible notice - replaces join button */}
        {isActiveRun && !isCreator && !isJoined && isAgeIneligible && (
          <div className="mb-4 p-4 bg-white rounded-xl border border-zinc-200 text-center" data-testid="age-ineligible-notice">
            <p className="text-sm text-zinc-500">
              This {runNoun.toLowerCase()} is for players {formatAgeRangeSentence(gameAgeMin, gameAgeMax)}. View only.
            </p>
          </div>
        )}
        {/* Join Run button - one button, no team picker. Backend per-team
            capacity is handled by the team_a → team_b 409 fallback. */}
        {showPlayerJoinAction && (
          <div className="mt-6 mb-6">
            {isWaitlisted ? (
              <div
                className="w-full min-h-12 rounded-xl font-semibold flex flex-col items-center justify-center gap-0.5 bg-amber-50 text-amber-700 border border-amber-200"
                data-testid="waitlist-status"
              >
                <span>You're on the Waitlist</span>
                {waitlistStatus.userPosition && (
                  <span className="text-xs font-medium text-amber-600">
                    Position #{waitlistStatus.userPosition}
                  </span>
                )}
              </div>
            ) : (
              <Button
                onClick={canJoinWaitlist ? handleJoinWaitlist : handleJoinRun}
                className={`w-full h-12 rounded-xl font-semibold transition-transform duration-100 active:scale-[0.98] active:brightness-95 ${
                  isRunFull && !isWaitlistEnabled
                    ? 'bg-zinc-200 text-zinc-600 hover:bg-zinc-200'
                    : 'bg-orange-500 text-white hover:bg-orange-600'
                }`}
                disabled={(isRunFull && !isWaitlistEnabled) || joiningTeam !== null || joiningWaitlist}
                data-testid={canJoinWaitlist ? 'join-waitlist-btn' : 'join-run-btn'}
              >
                {joiningTeam !== null || joiningWaitlist
                  ? (joiningWaitlist ? 'Joining Waitlist...' : 'Joining...')
                  : isRunFull
                    ? (isWaitlistEnabled ? 'Join Waitlist' : `${runNoun} is full`)
                    : `Join ${runNoun} (${totalPlayers} / ${effectiveMaxPlayers})`}
              </Button>
            )}

            {/* Confirmation Expectation Line */}
            <p className="text-xs text-zinc-500 mt-2">
              {isWaitlisted
                ? "We'll notify you if a spot opens."
                : "You'll confirm your spot 12 hours before tip-off."}
            </p>
          </div>
        )}

        {/* Leave Button - moved to page bottom after comments */}

        {/* Creator Actions for Cancelled games - Reopen option */}
        {isCreator && game.status === 'cancelled' && (
          <div className="flex gap-3 mb-6">
            <Button
              onClick={handleReopenGame}
              className="flex-1 h-12 rounded-xl font-semibold bg-orange-500 hover:bg-orange-600 text-white gap-2 transition-transform duration-100 active:scale-[0.98] active:brightness-95"
            >
              <RefreshCw className="w-4 h-4" />
              Reopen {runNoun}
            </Button>
            <Button
              onClick={() => navigate('/create-game', { state: { cloneFrom: game } })}
              variant="outline"
              className="flex-1 h-12 rounded-xl font-medium border-zinc-200 text-zinc-600 hover:bg-zinc-50 hover:border-zinc-300 gap-2 transition-transform duration-100 active:scale-[0.98] active:brightness-95"
              data-testid="duplicate-run-btn"
            >
              <Copy className="w-4 h-4" />
              Duplicate {runNoun}
            </Button>
          </div>
        )}
        
        {/* Host Fill Banner - Show when run is ≥70% full */}
        <HostFillBanner run={game} isHost={canUseHostTools} />
        
        {/* Invite Players for non-creators who have joined (active runs only) */}
        {!canUseHostTools && isJoined && isActiveRun && (
          <div className="mb-6">
            <Button 
              onClick={() => setShareSheetOpen(true)}
              className="w-full h-12 rounded-xl font-semibold bg-orange-500 hover:bg-orange-600 text-white gap-2 transition-transform duration-100 active:scale-[0.98] active:brightness-95"
              data-testid="share-run-btn-joined"
            >
              <Share2 className="w-4 h-4" />
              Invite Friends
            </Button>
          </div>
        )}
        
        {/* Duplicate Run for completed games */}
        {isCreator && game.status === 'completed' && (
          <div className="mb-6">
            <Button
              onClick={() => navigate('/create-game', { state: { cloneFrom: game } })}
              className="w-full h-12 rounded-xl font-semibold bg-orange-500 hover:bg-orange-600 text-white gap-2"
              data-testid="duplicate-run-btn"
            >
              <Copy className="w-4 h-4" />
              Run It Back (Duplicate {runNoun})
            </Button>
          </div>
        )}

        {/* Game Outcome Submission - Attendance First.
            Shows for the host once the run is awaiting outcome (past, no outcome)
            OR a legacy run already flipped to status="completed" without outcome
            data. Backend submit_game_outcome accepts both (no status precondition). */}
        {SHOW_GAME_OUTCOME_SUBMISSION_UI && isCreator && (isAwaitingOutcome || (game.status === 'completed' && !game.completion_status && !game.result_winner)) && (
          <div className="mt-6 p-5 bg-zinc-50 rounded-xl" data-testid="outcome-submission-card">
            <p className="text-base font-semibold text-zinc-900 mb-4">Submit Game Outcome</p>
            
            {outcomeStep === 'attendance' && (
              <div className="space-y-4">
                {/* Team A Participation */}
                <div className="space-y-2">
                  <p className="text-sm font-medium text-zinc-700">Did Team A participate?</p>
                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      onClick={() => setTeamAParticipated(true)}
                      className={`h-12 rounded-xl font-semibold transition-all ${
                        teamAParticipated === true 
                          ? 'bg-green-500 text-white hover:bg-green-600' 
                          : 'bg-white text-zinc-700 border border-zinc-300 hover:bg-zinc-50'
                      }`}
                      data-testid="team-a-yes-btn"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Yes
                    </Button>
                    <Button
                      onClick={() => setTeamAParticipated(false)}
                      className={`h-12 rounded-xl font-semibold transition-all ${
                        teamAParticipated === false 
                          ? 'bg-red-500 text-white hover:bg-red-600' 
                          : 'bg-white text-zinc-700 border border-zinc-300 hover:bg-zinc-50'
                      }`}
                      data-testid="team-a-no-btn"
                    >
                      <X className="w-4 h-4 mr-2" />
                      No
                    </Button>
                  </div>
                </div>

                {/* Team B Participation */}
                <div className="space-y-2">
                  <p className="text-sm font-medium text-zinc-700">Did Team B participate?</p>
                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      onClick={() => setTeamBParticipated(true)}
                      className={`h-12 rounded-xl font-semibold transition-all ${
                        teamBParticipated === true 
                          ? 'bg-green-500 text-white hover:bg-green-600' 
                          : 'bg-white text-zinc-700 border border-zinc-300 hover:bg-zinc-50'
                      }`}
                      data-testid="team-b-yes-btn"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Yes
                    </Button>
                    <Button
                      onClick={() => setTeamBParticipated(false)}
                      className={`h-12 rounded-xl font-semibold transition-all ${
                        teamBParticipated === false 
                          ? 'bg-red-500 text-white hover:bg-red-600' 
                          : 'bg-white text-zinc-700 border border-zinc-300 hover:bg-zinc-50'
                      }`}
                      data-testid="team-b-no-btn"
                    >
                      <X className="w-4 h-4 mr-2" />
                      No
                    </Button>
                  </div>
                </div>

                {/* Continue Button */}
                {teamAParticipated !== null && teamBParticipated !== null && (
                  <Button
                    onClick={() => {
                      if (teamAParticipated && teamBParticipated) {
                        setOutcomeStep('winner');
                      } else {
                        setOutcomeStep('confirm');
                      }
                    }}
                    className="w-full h-12 mt-4 rounded-xl font-semibold bg-orange-500 text-white hover:bg-orange-600"
                    data-testid="continue-outcome-btn"
                  >
                    Continue
                  </Button>
                )}
              </div>
            )}

            {outcomeStep === 'winner' && teamAParticipated && teamBParticipated && (
              <div className="space-y-4">
                <p className="text-sm font-medium text-zinc-700">Who won? (Optional)</p>
                <div className="grid grid-cols-1 gap-3">
                  <Button
                    onClick={() => setWinnerTeam('team_a')}
                    className={`h-12 rounded-xl font-semibold transition-all ${
                      winnerTeam === 'team_a' 
                        ? 'bg-orange-500 text-white hover:bg-orange-600' 
                        : 'bg-white text-zinc-700 border border-zinc-300 hover:bg-zinc-50'
                    }`}
                    data-testid="winner-team-a-btn"
                  >
                    Team A Won
                  </Button>
                  <Button
                    onClick={() => setWinnerTeam('team_b')}
                    className={`h-12 rounded-xl font-semibold transition-all ${
                      winnerTeam === 'team_b' 
                        ? 'bg-orange-500 text-white hover:bg-orange-600' 
                        : 'bg-white text-zinc-700 border border-zinc-300 hover:bg-zinc-50'
                    }`}
                    data-testid="winner-team-b-btn"
                  >
                    Team B Won
                  </Button>
                  <Button
                    onClick={() => setWinnerTeam('none')}
                    className={`h-12 rounded-xl font-semibold transition-all ${
                      winnerTeam === 'none' 
                        ? 'bg-zinc-600 text-white hover:bg-zinc-700' 
                        : 'bg-white text-zinc-700 border border-zinc-300 hover:bg-zinc-50'
                    }`}
                    data-testid="no-winner-btn"
                  >
                    No Winner Recorded
                  </Button>
                </div>
                <div className="flex gap-3 mt-4">
                  <Button
                    onClick={() => setOutcomeStep('attendance')}
                    variant="outline"
                    className="flex-1 h-12 rounded-xl font-semibold"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={() => setOutcomeStep('confirm')}
                    disabled={winnerTeam === null}
                    className="flex-1 h-12 rounded-xl font-semibold bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50"
                    data-testid="confirm-winner-btn"
                  >
                    Continue
                  </Button>
                </div>
              </div>
            )}

            {outcomeStep === 'confirm' && (
              <div className="space-y-4">
                {/* Summary */}
                <div className="p-4 bg-white rounded-lg border border-zinc-200">
                  <p className="text-sm font-semibold text-zinc-900 mb-3">Confirm Outcome</p>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-zinc-600">Team A:</span>
                      <span className={teamAParticipated ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                        {teamAParticipated ? 'Participated' : 'No-show'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-zinc-600">Team B:</span>
                      <span className={teamBParticipated ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                        {teamBParticipated ? 'Participated' : 'No-show'}
                      </span>
                    </div>
                    {teamAParticipated && teamBParticipated && (
                      <div className="flex justify-between pt-2 border-t border-zinc-100">
                        <span className="text-zinc-600">Winner:</span>
                        <span className="font-medium text-zinc-900">
                          {winnerTeam === 'team_a' && 'Team A'}
                          {winnerTeam === 'team_b' && 'Team B'}
                          {winnerTeam !== 'team_a' && winnerTeam !== 'team_b' && 'Not recorded'}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* No-show warning */}
                  {(!teamAParticipated || !teamBParticipated) && (
                    <div className="mt-3 p-3 bg-amber-50 rounded-lg flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-amber-800">
                        {!teamAParticipated && !teamBParticipated && 'Both teams no-show will be recorded.'}
                        {!teamAParticipated && teamBParticipated && 'Team A no-show will be recorded.'}
                        {teamAParticipated && !teamBParticipated && 'Team B no-show will be recorded.'}
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={() => setOutcomeStep(teamAParticipated && teamBParticipated ? 'winner' : 'attendance')}
                    variant="outline"
                    className="flex-1 h-12 rounded-xl font-semibold"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={async () => {
                      setIsSubmittingOutcome(true);
                      try {
                        await apiClient.post(`/games/${game.id}/outcome`, 
                          { 
                            team_a_participated: teamAParticipated,
                            team_b_participated: teamBParticipated,
                            winner_team: teamAParticipated && teamBParticipated ? winnerTeam : null
                          },
                          { headers: { 'Idempotency-Key': `outcome-${game.id}-${Date.now()}` } }
                        );
                        toast.success('Game outcome submitted!');
                        // Reset state
                        setOutcomeStep('attendance');
                        setTeamAParticipated(null);
                        setTeamBParticipated(null);
                        setWinnerTeam(null);
                        fetchGame();
                      } catch (error) {
                        toast.error(error.response?.data?.detail || 'Failed to submit outcome');
                      } finally {
                        setIsSubmittingOutcome(false);
                      }
                    }}
                    disabled={isSubmittingOutcome}
                    className="flex-1 h-12 rounded-xl font-semibold bg-orange-500 text-white hover:bg-orange-600"
                    data-testid="submit-outcome-btn"
                  >
                    {isSubmittingOutcome ? 'Submitting...' : 'Submit Outcome'}
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Show outcome if already submitted */}
        {game.completion_status && (
          <div className="mt-6 p-4 bg-zinc-50 rounded-xl" data-testid="outcome-display">
            <p className="text-sm font-semibold text-zinc-900 mb-2">Game Outcome</p>
            <div className="space-y-1 text-sm">
              {game.completion_status === 'completed' ? (
                <>
                  <p className="text-green-600 font-medium">Game Completed</p>
                  {game.result_winner && (
                    <p className="text-zinc-700">
                      Winner: {game.result_winner === 'team_a' ? 'Team A' : 'Team B'}
                    </p>
                  )}
                  {!game.result_winner && (
                    <p className="text-zinc-500">No winner recorded</p>
                  )}
                </>
              ) : game.completion_status === 'team_a_no_show' ? (
                <p className="text-amber-600 font-medium">Team A No-Show</p>
              ) : game.completion_status === 'team_b_no_show' ? (
                <p className="text-amber-600 font-medium">Team B No-Show</p>
              ) : game.completion_status === 'both_no_show' ? (
                <p className="text-red-600 font-medium">Both Teams No-Show</p>
              ) : (
                <p className="text-zinc-600">{game.completion_status}</p>
              )}
            </div>
          </div>
        )}

        {/* Legacy: Show result if submitted via old endpoint */}
        {!game.completion_status && game.result_winner && (
          <div className="mt-6 p-4 bg-zinc-50 rounded-xl">
            <p className="text-sm text-zinc-700">
              Winner: {game.result_winner === 'team_a' ? 'Team A' : 'Team B'}
            </p>
          </div>
        )}

        {/* Roster Section */}
        <div className="mt-4">
          {/* Post-Game Follow Prompt - Show for completed games */}
          {game.status === 'completed' && game.completion_status && showPostGamePrompt && (
            <div className="mb-6">
              <PostGameFollowPrompt 
                game={game} 
                onDismiss={() => setShowPostGamePrompt(false)} 
              />
            </div>
          )}

          {/* Confirmation Summary + CTA for current user */}
          {Object.keys(confirmationMap).length > 0 && (
            <div className="mb-6 p-3 bg-zinc-50 rounded-xl border border-zinc-100" data-testid="confirmation-summary">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-green-500" />
                  <span className="text-sm font-medium text-zinc-700">
                    {confirmationSummary.confirmed} of {confirmationSummary.total} confirmed
                  </span>
                </div>
                {/* Confirm CTA gated by the live confirmation window. Backend
                    (confirmation_window.state) is authoritative; local helper is
                    a render-time fallback only. */}
                {user && (() => {
                  const allPlayerIds = [...teamA, ...teamB].map(p => p.user_id);
                  const isInGame = allPlayerIds.includes(user.id);
                  if (!isInGame) return null;
                  const myConf = confirmationMap[user.id];
                  const myStatus = myConf?.status;
                  // Terminal confirmed state.
                  if (myStatus === 'confirmed') {
                    return (
                      <span className="flex items-center gap-1 text-sm font-medium text-green-600" data-testid="confirm-confirmed-state">
                        <CheckCircle2 className="w-4 h-4" /> Confirmed
                      </span>
                    );
                  }
                  // Allow-list mirrors backend's confirmable statuses exactly.
                  const CONFIRMABLE_STATUSES = ['pending', 'reminder_sent', 'final_reminder_sent'];
                  if (!CONFIRMABLE_STATUSES.includes(myStatus)) return null;
                  // Window gate — prefer backend state, fall back to local compute.
                  const windowState = confirmationWindow?.state
                    || getConfirmationWindowState({ date_time: game?.date_time, status: game?.status });
                  if (windowState === CONFIRMATION_WINDOW.BEFORE) {
                    return (
                      <span className="text-xs text-zinc-500 text-right" data-testid="confirm-window-before">
                        Confirmation opens 24 hours before the run.
                      </span>
                    );
                  }
                  if (windowState === CONFIRMATION_WINDOW.CLOSED) {
                    return (
                      <span className="text-xs text-zinc-500" data-testid="confirm-window-closed">
                        Confirmation closed
                      </span>
                    );
                  }
                  if (windowState !== CONFIRMATION_WINDOW.OPEN) return null; // blocked / invalid → hide
                  return (
                    <Button
                      size="sm"
                      className="bg-orange-500 hover:bg-orange-600 text-white rounded-full h-8 px-3 text-xs transition-transform duration-100 active:scale-[0.98] active:brightness-95"
                      onClick={handleConfirmSpot}
                      disabled={isConfirming}
                      data-testid="confirm-spot-btn"
                    >
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                      {isConfirming ? 'Confirming...' : 'Confirm My Spot'}
                    </Button>
                  );
                })()}
              </div>
            </div>
          )}
          
          {/* ROSTER - Team A */}
          {/* Hide detailed roster for minors viewing adult-only runs */}
          {isMinor && isAdultOnlyRun && isAgeRestricted ? (
            <div className="mb-8 p-4 bg-zinc-50 rounded-xl border border-zinc-200">
              <div className="flex items-center gap-3">
                <Lock className="w-5 h-5 text-zinc-400" />
                <div>
                  <p className="text-sm font-medium text-zinc-700">Roster Hidden</p>
                  <p className="text-xs text-zinc-500">Player details are not available for age-restricted runs.</p>
                </div>
              </div>
            </div>
          ) : (
          <>
          {/* PLAYERS section header */}
          <div className="flex items-baseline justify-between mb-3 mt-2" data-testid="players-section-header">
            <h2 className="text-lg font-bold text-zinc-900">Players</h2>
            <p className="text-sm text-zinc-500" data-testid="players-count-label">
              {totalPlayers >= effectiveMaxPlayers ? (
                <span>{totalPlayers}/{effectiveMaxPlayers} going · Full</span>
              ) : isPreLockIn && totalPlayers === 0 ? (
                <span>Needs {minPlayers} {minPlayers === 1 ? 'player' : 'players'}</span>
              ) : isPreLockIn ? (
                <span>{totalPlayers} joined · {stillNeeded} more needed</span>
              ) : (
                <span>{totalPlayers}/{effectiveMaxPlayers} going · {effectiveMaxPlayers - totalPlayers} {effectiveMaxPlayers - totalPlayers === 1 ? 'spot' : 'spots'} open</span>
              )}
              {game?.invites_summary?.pending_count > 0 && (
                <span data-testid="invited-count-chip"> · {game.invites_summary.pending_count} invited</span>
              )}
            </p>
          </div>

          {/* Host/cohost pending list. The paginated endpoint independently
              authorizes every page; the summary count is not an access grant. */}
          {canUseHostTools && game?.invites_summary?.pending_count > 0 && (
            <div className="mb-4" data-testid="invited-list-section">
              <button
                type="button"
                onClick={() => setInvitedExpanded(v => !v)}
                className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-zinc-50 border border-zinc-200 hover:bg-zinc-100 transition-colors text-left"
                data-testid="invited-list-toggle"
                aria-expanded={invitedExpanded}
              >
                <span className="text-sm font-semibold text-zinc-700">
                  Invited ({game.invites_summary.pending_count})
                </span>
                <ChevronDown
                  className={`w-4 h-4 text-zinc-400 transition-transform ${invitedExpanded ? 'rotate-180' : ''}`}
                />
              </button>
              {invitedExpanded && (
                <PendingInvitationList gameId={id} refreshKey={game} testId="invited-list"
                  renderPlayer={(inv) => (
                    <li
                      key={inv.user_id}
                      className="flex items-center gap-3 px-3 py-2 rounded-lg bg-white border border-zinc-100"
                      data-testid={`invited-row-${inv.user_id}`}
                    >
                      {inv.profile_image_url ? (
                        <img
                          src={resolveAvatarUrl(inv.profile_image_url)}
                          alt=""
                          className="w-8 h-8 rounded-full object-cover bg-zinc-100"
                        />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-orange-100 text-orange-600 flex items-center justify-center font-medium text-sm">
                          {(inv.display_name || '?').charAt(0).toUpperCase()}
                        </div>
                      )}
                      <span className="flex-1 text-sm font-medium text-zinc-900 truncate">
                        {inv.display_name}
                      </span>
                      <span className="text-xs text-zinc-500">Pending</span>
                    </li>
                  )}
                />
              )}
            </div>
          )}

          {/* ROSTER - Unified Open Pickup list. team_a + team_b combined, sorted by confirmation status. */}
          <div className="mb-5">
            {Object.keys(confirmationMap).length > 0 && (() => {
              const totalConfirmed = sortedRoster.filter(p => confirmationMap[p.user_id]?.status === 'confirmed').length;
              return totalConfirmed > 0 ? (
                <div className="flex items-center justify-end mb-2">
                  <span className="text-xs text-green-600 font-medium" data-testid="roster-confirmed-count">
                    <CheckCircle2 className="w-3.5 h-3.5 inline mr-0.5" />
                    {totalConfirmed}/{sortedRoster.length} confirmed
                  </span>
                </div>
              ) : null;
            })()}
            {sortedRoster.length === 0 && placeholdersShow === 0 ? (
              <p className="text-sm text-zinc-400">No players yet</p>
            ) : (
              <div className="divide-y divide-zinc-100" data-testid="players-roster-list">
                {sortedRoster.map((player, idx) => {
                  const conf = confirmationMap[player.user_id];
                  const isConfirmed = conf?.status === 'confirmed';
                  const isPending = conf && conf.status !== 'confirmed';
                  return (
                  <div
                    key={player.user_id || `roster-${idx}`}
                    className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0"
                    data-testid={`players-roster-player-${idx}`}
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      {/* Avatar with confirmation ring */}
                      <div className={`w-11 h-11 rounded-full flex items-center justify-center text-sm font-medium relative flex-shrink-0 overflow-hidden
                        ${isConfirmed ? 'bg-green-50 text-green-700 ring-2 ring-green-300' : 'bg-zinc-100 text-zinc-600'}`}>
                        {/* Initial letter sits underneath as a fallback that's always rendered. */}
                        <span>{player.username?.charAt(0).toUpperCase() || 'P'}</span>
                        {/* Real avatar overlays the initial. On 404 / load error we hide it
                            so the initial underneath remains visible. */}
                        {(() => {
                          const raw = player.profile_image_url || `/api/avatars/${player.user_id}`;
                          const src = raw?.startsWith?.('http')
                            ? raw
                            : `${process.env.REACT_APP_BACKEND_URL || ''}${raw}`;
                          return (
                            <img
                              src={src}
                              alt=""
                              loading="lazy"
                              onError={(e) => { e.currentTarget.style.display = 'none'; }}
                              className="absolute inset-0 w-full h-full object-cover rounded-full"
                              data-testid={`roster-avatar-img-${player.user_id}`}
                            />
                          );
                        })()}
                        {isConfirmed && (
                          <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-green-500 rounded-full flex items-center justify-center z-10" data-testid={`confirmed-badge-${player.user_id}`}>
                            <Check className="w-2.5 h-2.5 text-white" />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p 
                          className="text-[15px] font-semibold text-zinc-900 hover:text-orange-600 cursor-pointer transition-colors truncate"
                          onClick={() => navigate(`/player/${player.username}`)}
                        >
                          {rosterPlayerLabel(player)}
                          {player.user_id === game.creator_id && (
                            <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-orange-50 text-orange-700 border border-orange-200 align-middle">Host</span>
                          )}
                        </p>
                        <div className="flex items-center gap-2 text-[13px] text-zinc-600">
                          {/* Confirmation badge */}
                          {isConfirmed ? (
                            <span className="text-green-600 font-medium" data-testid={`player-confirmed-${player.user_id}`}>Confirmed</span>
                          ) : isPending ? (
                            <span className="text-amber-600 font-medium" data-testid={`player-pending-${player.user_id}`}>
                              <Clock className="w-3 h-3 inline mr-0.5" />Pending
                            </span>
                          ) : (
                            <span className="text-zinc-500">{player.joined_via === 'invite' ? 'Invited' : 'Joined'}</span>
                          )}

                          {/* Attendance Confidence chip — low-emphasis, never shows raw %. */}
                          {player.confidence_label && (
                            <>
                              <span>•</span>
                              <span
                                className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-medium ${confidenceChipClass(player.confidence_label)}`}
                                data-testid={`player-confidence-${player.user_id}`}
                              >
                                {player.confidence_label}
                              </span>
                            </>
                          )}

                          {/* Runs played count - always show if available */}
                          {player.games_played !== undefined && player.games_played > 0 && (
                            <>
                              <span>•</span>
                              <span>{player.games_played} {player.games_played === 1 ? 'run' : 'runs'}</span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                    {/* Follow button - don't show for self; hidden in admin support-view */}
                    {user && player.user_id !== user.id && !isSupportViewer && (
                      <PlayerFollowButton
                        userId={player.user_id}
                        username={player.username}
                        isFollowing={followStatusMap[player.user_id]?.following || false}
                        isRequested={followStatusMap[player.user_id]?.requested || false}
                        onFollowChange={handleFollowChange}
                        size="sm"
                      />
                    )}
                  </div>
                  );
                })}
                {/* Pre-lock-in placeholders. Rendered only while the run is
                    below min_players so the list feels close to viable
                    without ghosting out the full capacity.
                    Each ghost shows the future roster slot number so users
                    can visually count toward viability. */}
                {placeholdersShow > 0 && Array.from({ length: placeholdersShow }).map((_, idx) => {
                  const slotNumber = sortedRoster.length + idx + 1;
                  return (
                    <div
                      key={`roster-placeholder-${idx}`}
                      className="flex items-center gap-3 py-3 first:pt-0 last:pb-0"
                      data-testid="roster-placeholder-row"
                    >
                      <div className="w-11 h-11 rounded-full border-2 border-dashed border-zinc-200 flex-shrink-0 flex items-center justify-center">
                        <span className="text-sm font-medium text-zinc-300">{slotNumber}</span>
                      </div>
                      <p className="text-sm text-zinc-400">Open spot</p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          </>
          )}
        </div>

        {/* Edit History - Collapsed Toggle (moved to lower section) */}
        {audits.length > 0 && (
          <details className="border-t border-zinc-100 mt-6 pt-4 group" data-testid="edit-history-section">
            <summary className="cursor-pointer list-none flex items-center justify-between text-sm text-zinc-500 hover:text-zinc-700 transition-colors select-none">
              <span data-testid="edit-history-toggle">View edit history ({audits.length})</span>
              <span className="text-xs text-zinc-400 group-open:rotate-180 transition-transform">▾</span>
            </summary>
            <div className="space-y-4 mt-4">
              {audits.slice(0, 5).map((audit, idx) => (
                <div key={audit.id || `audit-${idx}`} className="bg-zinc-50 rounded-lg p-3 space-y-2" data-testid={`edit-history-${idx}`}>
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-zinc-700">Edited by {audit.editor_username}</p>
                    <p className="text-xs text-zinc-400">{formatLocalDisplay(audit.timestamp, userTz)}</p>
                  </div>
                  {audit.changes && Object.keys(audit.changes).length > 0 ? (
                    <div className="space-y-1.5">
                      {Object.entries(audit.changes).map(([field, change]) => {
                        const label = FIELD_LABELS[field] || field;
                        const oldVal = formatChangeValue(field, change?.old, userTz);
                        const newVal = formatChangeValue(field, change?.new, userTz);
                        return (
                          <div key={field} className="flex items-start gap-2 text-xs" data-testid={`change-${field}`}>
                            <span className="font-medium text-zinc-500 min-w-[80px] flex-shrink-0">{label}</span>
                            <span className="text-red-500 line-through">{oldVal || '—'}</span>
                            <span className="text-zinc-400">→</span>
                            <span className="text-green-700 font-medium">{newVal || '—'}</span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-xs text-zinc-400">Details not recorded</p>
                  )}
                </div>
              ))}
            </div>
          </details>
        )}

        {/* Run Comments Section */}
        {isMinor && isAdultOnlyRun && isAgeRestricted ? (
          <div className="border-t border-zinc-100 mt-8 pt-8" data-testid="comments-section-restricted">
            <div className="flex items-center gap-2 mb-4">
              <MessageCircle className="w-5 h-5 text-zinc-600" />
              <p className="text-base font-semibold text-zinc-900">{runNoun} Comments</p>
            </div>
            <div className="p-4 bg-zinc-50 rounded-xl border border-zinc-200">
              <div className="flex items-center gap-3">
                <Lock className="w-5 h-5 text-zinc-400" />
                <div>
                  <p className="text-sm font-medium text-zinc-700">Comments Hidden</p>
                  <p className="text-xs text-zinc-500">Comments are not available for age-restricted runs.</p>
                </div>
              </div>
            </div>
          </div>
        ) : (
        <div className="bg-white rounded-xl border border-zinc-200 shadow-[0_1px_2px_rgba(0,0,0,0.04)] p-4 mt-4" data-testid="comments-section">
          <div className="flex items-center gap-2 mb-4">
            <MessageCircle className="w-4 h-4 text-zinc-500" />
            <p className="text-sm font-bold text-zinc-900 uppercase tracking-wide">Chat</p>
            <span className="text-xs text-zinc-400">({comments.length})</span>
          </div>

          {/* Comments List */}
          {loadingComments ? (
            <p className="text-sm text-zinc-500 py-4">Loading comments...</p>
          ) : comments.length === 0 ? (
            <p className="text-sm text-zinc-500 py-4">No comments yet. Be the first to coordinate!</p>
          ) : (
            <div className="space-y-4 mb-6">
              {comments.map((comment) => (
                <div key={comment.id} className="group" data-testid={`comment-${comment.id}`}>
                  <div className="flex items-start gap-3">
                    {/* Avatar */}
                    <div className="w-8 h-8 rounded-full bg-zinc-200 flex items-center justify-center text-zinc-600 text-xs font-medium flex-shrink-0">
                      {comment.user_display_name?.charAt(0).toUpperCase() || 'U'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-medium text-zinc-900">{comment.user_display_name}</span>
                        <span className="text-xs text-zinc-400">—</span>
                        <span className="text-xs text-zinc-500">
                          {(() => {
                            try {
                              const ts = comment.created_at;
                              const date = new Date(ts && !ts.endsWith('Z') && !/[+-]\d{2}:\d{2}$/.test(ts) ? ts + 'Z' : ts);
                              return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                            } catch {
                              return '';
                            }
                          })()}
                        </span>
                        {/* Delete button - show for own comments or if host; hidden in admin support-view */}
                        {!isSupportViewer && (comment.user_id === user?.id || canUseHostTools) && (
                          <button
                            onClick={() => deleteComment(comment.id)}
                            className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-red-50 rounded"
                            title="Delete comment"
                            data-testid={`delete-comment-${comment.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5 text-red-500" />
                          </button>
                        )}
                      </div>
                      <p className="text-sm text-zinc-700 whitespace-pre-wrap break-words">{comment.text}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Comment Input */}
          {(() => {
            // Admin support-view: suppress the composer entirely (read-only).
            if (isSupportViewer) return null;
            const teamAIds = (game?.team_a_players || []).map(p => p.user_id);
            const teamBIds = (game?.team_b_players || []).map(p => p.user_id);
            const isOnRoster = teamAIds.includes(user?.id) || teamBIds.includes(user?.id);
            const canComment = canUseHostTools || isOnRoster;

            if (!canComment) {
              return (
                <div className="p-4 bg-zinc-50 rounded-xl text-center">
                  <p className="text-sm text-zinc-600">Join this {runNoun.toLowerCase()} to comment.</p>
                </div>
              );
            }

            return (
              <div className="flex items-end gap-2">
                {/* User avatar */}
                <div className="w-8 h-8 rounded-full bg-zinc-200 flex items-center justify-center text-zinc-600 text-xs font-semibold flex-shrink-0 mb-0.5">
                  {user?.username?.charAt(0).toUpperCase() || 'U'}
                </div>
                <div className="flex-1 flex items-end gap-1.5 bg-zinc-100 rounded-2xl pl-4 pr-1.5 py-1.5">
                  <input
                    type="text"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value.slice(0, 300))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey && newComment.trim()) {
                        e.preventDefault();
                        postComment();
                      }
                    }}
                    placeholder="Message..."
                    className="flex-1 bg-transparent text-sm py-1.5 focus:outline-none placeholder:text-zinc-400"
                    maxLength={300}
                    disabled={isPostingComment}
                    data-testid="comment-input"
                  />
                  <button
                    onClick={postComment}
                    disabled={!newComment.trim() || isPostingComment}
                    className="w-8 h-8 rounded-full bg-orange-500 text-white flex items-center justify-center disabled:opacity-30 transition-opacity flex-shrink-0"
                    data-testid="post-comment-btn"
                  >
                    <Send className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })()}
          
          {/* Character count */}
          {newComment.length > 0 && (
            <p className={`text-xs mt-2 ${newComment.length > 280 ? 'text-amber-600' : 'text-zinc-400'}`}>
              {newComment.length}/300
            </p>
          )}
        </div>
        )}

        {/* Leave Run - hidden once the run has started (roster locked). Backend
            enforces the lock; this is convenience display only. */}
        {isJoined && !isCreator && game.status !== 'completed' && game.status !== 'cancelled' && (
          isPastRun(game.date_time) ? (
            <div className="mt-6 mb-4 px-1 pb-safe">
              <p className="text-xs text-zinc-500 text-center" data-testid="roster-locked-note">
                This {runNoun.toLowerCase()} has started — the roster is locked.
              </p>
            </div>
          ) : (
            <div className="mt-6 mb-4 px-1 pb-safe">
              <button
                onClick={handleLeaveGame}
                disabled={isLeaving}
                className="w-full h-12 rounded-xl text-[15px] font-semibold text-red-600 bg-white border border-red-200 hover:bg-red-50 hover:border-red-300 transition-all duration-100 active:scale-[0.98] active:brightness-95 disabled:opacity-60"
                data-testid="leave-game-btn"
              >
                {isLeaving ? 'Leaving...' : `Leave This ${runNoun}`}
              </button>
            </div>
          )
        )}
      </div>
      {showCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="bg-white p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Cancel {runNoun}</h2>
            <p className="text-gray-600 mb-4">
              Are you sure you want to cancel this {runNoun.toLowerCase()}? Players will be notified.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Reason (optional)
              </label>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500"
                rows="3"
                placeholder="e.g., Weather, injury, not enough players..."
              />
            </div>
            <div className="flex gap-3">
              <Button
                onClick={handleCancelGame}
                disabled={isCancelling}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white transition-transform duration-100 active:scale-[0.98] active:brightness-95"
              >
                {isCancelling ? 'Cancelling...' : `Cancel ${runNoun}`}
              </Button>
              <Button
                onClick={() => {
                  setShowCancelModal(false);
                  setCancelReason('');
                }}
                variant="outline"
                className="flex-1"
              >
                Keep {runNoun}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Admin Cancel Game Modal */}
      {showAdminCancelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="bg-white p-6 max-w-md w-full">
            <div className="flex items-center gap-2 mb-4">
              <Shield className="w-6 h-6 text-purple-600" />
              <h2 className="text-2xl font-bold text-gray-900">Admin: Cancel {runNoun}</h2>
            </div>
            <p className="text-gray-600 mb-4">
              You are cancelling this {runNoun.toLowerCase()} as an admin. This action will be logged.
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason (optional)
              </label>
              <textarea
                value={adminCancelReason}
                onChange={(e) => setAdminCancelReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-purple-500"
                rows="3"
                placeholder="Reason for cancellation..."
              />
            </div>
            <div className="flex gap-3">
              <Button
                onClick={handleAdminCancelGame}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                Cancel {runNoun}
              </Button>
              <Button
                onClick={() => {
                  setShowAdminCancelModal(false);
                  setAdminCancelReason('');
                }}
                variant="outline"
                className="flex-1"
              >
                Keep {runNoun}
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Edit Game Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="bg-white p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Edit {runNoun}</h2>
            <form onSubmit={handleEditSubmit} className="space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                <input
                  type="text"
                  value={editForm.title || ''}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500"
                  required
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={editForm.description || ''}
                  onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500"
                  rows="3"
                />
              </div>

              <div>
                <div className="flex items-center justify-between gap-3 mb-1">
                  <label className="block text-sm font-medium text-gray-700">Public share message</label>
                  <span className="text-xs text-zinc-500">
                    {(editForm.public_share_message || '').length}/180
                  </span>
                </div>
                <textarea
                  value={editForm.public_share_message || ''}
                  onChange={(e) => setEditForm({ ...editForm, public_share_message: e.target.value })}
                  maxLength={180}
                  placeholder="Friday night run. Need 8 more. Grab a spot."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500"
                  rows="3"
                  data-testid="edit-public-share-message-input"
                />
                <p className="text-[13px] text-zinc-600 mt-1">
                  Visible on shared links and Instagram cards. Keep it public.
                </p>
              </div>

              {game?.creator_id === user?.id && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Post to Clubs <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  {loadingEditClubOptions ? (
                    <div className="rounded-[12px] border border-zinc-200 bg-zinc-50 p-3 text-sm text-zinc-500">
                      Loading clubs...
                    </div>
                  ) : editClubOptions.length > 0 ? (
                    <>
                      <div
                        className="mt-2 space-y-2 rounded-[12px] border border-zinc-200 bg-white p-2"
                        data-testid="edit-club-multiselect"
                      >
                        {editClubOptions.map((club) => {
                          const selected = (editForm.run_club_ids || []).includes(club.id);
                          return (
                            <button
                              key={club.id}
                              type="button"
                              onClick={() => toggleEditClub(club.id)}
                              className={`w-full flex items-center justify-between gap-3 rounded-[10px] px-3 py-2 text-left transition-colors ${
                                selected
                                  ? 'bg-orange-50 text-zinc-950 ring-1 ring-orange-200'
                                  : 'bg-white text-zinc-700 hover:bg-zinc-50'
                              }`}
                              data-testid={`edit-club-option-${club.id}`}
                              aria-pressed={selected}
                            >
                              <span className="min-w-0 truncate text-sm font-medium">{club.name}</span>
                              <span
                                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[11px] font-bold ${
                                  selected
                                    ? 'border-orange-500 bg-orange-500 text-white'
                                    : 'border-zinc-300 text-transparent'
                                }`}
                                aria-hidden="true"
                              >
                                ✓
                              </span>
                            </button>
                          );
                        })}
                      </div>
                      {(editForm.run_club_ids || []).length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-2" data-testid="edit-selected-club-chips">
                          {(editForm.run_club_ids || []).map((clubId) => {
                            const club = editClubOptions.find((item) => item.id === clubId);
                            if (!club) return null;
                            return (
                              <button
                                key={clubId}
                                type="button"
                                onClick={() => toggleEditClub(clubId)}
                                className="inline-flex items-center gap-1 rounded-full bg-zinc-100 px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-200"
                                data-testid={`edit-selected-club-chip-${clubId}`}
                              >
                                {club.name}
                                <span className="text-zinc-500" aria-hidden="true">×</span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="rounded-[12px] border border-zinc-200 bg-zinc-50 p-3 text-sm text-zinc-500">
                      No manageable clubs available.
                    </div>
                  )}
                  <p className="text-[13px] text-zinc-600 mt-1">
                    Club runs appear on selected club pages. Public runs may also appear in the main feed.
                  </p>
                </div>
              )}

              {/* Date & Time */}
              <GameTimePicker
                value={editForm.date_time_input || ''}
                onChange={(val) => setEditForm({ ...editForm, date_time_input: val })}
                label="Date & Time *"
                required
                className="mt-4"
              />
              {isEditDateTimeInPast && (
                <p className="text-sm text-red-500 -mt-2" data-testid="edit-past-date-error">
                  Game time must be in the future
                </p>
              )}

              {/* Location Name with Autocomplete */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Location Name *</label>
                <AddressAutocomplete
                  value={locInput}
                  onTyping={(val) => {
                    setLocInput(val);
                    setSelectedPlace(null);
                  }}
                  onSelect={(placeData) => {
                    setLocInput(placeData.inputLabel || placeData.location_name);
                    setSelectedPlace({
                      label: placeData.inputLabel || placeData.location_name,
                      place_id: placeData.place_id,
                      lat: placeData.lat,
                      lng: placeData.lng
                    });
                    // Update form with derived fields from Google Places
                    setEditForm(prev => ({
                      ...prev,
                      address: placeData.formatted_address || '',
                      city: placeData.city || '',
                      zip_code: placeData.zip_code || ''
                    }));
                  }}
                />
                {selectedPlace && (
                  <p className="text-xs text-green-600 mt-1">
                    ✓ Location updated (lat: {selectedPlace.lat?.toFixed(4)}, lng: {selectedPlace.lng?.toFixed(4)})
                  </p>
                )}
              </div>

              {/* Address (read-only if from autocomplete) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <input
                  type="text"
                  value={editForm.address || ''}
                  onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 focus:ring-2 focus:ring-orange-500"
                  placeholder="Selected from location autocomplete"
                />
              </div>

              {/* City & Zip (read-only) */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                  <input
                    type="text"
                    value={editForm.city || ''}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Zip Code</label>
                  <input
                    type="text"
                    value={editForm.zip_code || ''}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50"
                  />
                </div>
              </div>

              {/* Skill Levels */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Min Skill Level *</label>
                  <select
                    value={editForm.skill_level_min || 'Unranked'}
                    onChange={(e) => setEditForm({ ...editForm, skill_level_min: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500"
                    required
                  >
                    <option value="Unranked">Unranked</option>
                    <option value="Role Player">Role Player</option>
                    <option value="Baller">Baller</option>
                    <option value="Elite">Elite</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Skill Level *</label>
                  <select
                    value={editForm.skill_level_max || 'Elite'}
                    onChange={(e) => setEditForm({ ...editForm, skill_level_max: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500"
                    required
                  >
                    <option value="Unranked">Unranked</option>
                    <option value="Role Player">Role Player</option>
                    <option value="Baller">Baller</option>
                    <option value="Elite">Elite</option>
                  </select>
                </div>
              </div>

              {/* Run Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Run Type *</label>
                <select
                  value={editForm.court_type || 'full_court'}
                  onChange={(e) => setEditForm({ ...editForm, court_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500"
                  data-testid="edit-court-type-select"
                  required
                >
                  <option value="full_court">Full Court</option>
                  <option value="half_court">Half Court</option>
                  <option value="training">Training</option>
                </select>
              </div>

              {/* Age Range */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Min Age *</label>
                  <input
                    type="number"
                    value={editForm.age_min || 13}
                    onChange={(e) => setEditForm({ ...editForm, age_min: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500"
                    min="13"
                    max="100"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Age *</label>
                  <input
                    type="number"
                    value={editForm.age_max || 99}
                    onChange={(e) => setEditForm({ ...editForm, age_max: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500"
                    min="13"
                    max="100"
                    required
                  />
                </div>
              </div>

              {/* Player Counts */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Min Players *</label>
                  <input
                    type="number"
                    value={editForm.min_players || 4}
                    onChange={(e) => setEditForm({ ...editForm, min_players: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500"
                    min="2"
                    max="50"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Max Players *</label>
                  <input
                    type="number"
                    value={editForm.max_players === '' || editForm.max_players === undefined ? '' : editForm.max_players}
                    onChange={(e) => {
                      const raw = e.target.value;
                      setEditForm({ ...editForm, max_players: raw === '' ? '' : parseInt(raw, 10) });
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500"
                    min="2"
                    max="98"
                    step="2"
                    required
                    data-testid="edit-max-players-input"
                  />
                  {editMaxPlayersError && (
                    <p className="mt-1 text-xs text-red-600" data-testid="edit-max-players-error">{editMaxPlayersError}</p>
                  )}
                </div>
              </div>

              {/* Visibility */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Run Visibility *</label>
                <select
                  value={editForm.is_private ? 'private' : 'public'}
                  onChange={(e) => {
                    const makePrivate = e.target.value === 'private';
                    setEditForm({ ...editForm, is_private: makePrivate, privacy: makePrivate ? 'private' : 'public' });
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-orange-500"
                  data-testid="edit-visibility-select"
                  required
                >
                  <option value="public">Public (Anyone can discover)</option>
                  <option value="private">Private (Invite/link only)</option>
                </select>
              </div>

              {isCreator && game.status !== 'completed' && game.status !== 'cancelled' && (
                <div className="pt-2">
                  {renderCoHostManagement()}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <Button 
                  type="submit" 
                  className="btn-primary flex-1 transition-transform duration-100 active:scale-[0.98] active:brightness-95"
                  disabled={isSaving || isEditDateTimeInPast || !!editMaxPlayersError}
                  data-testid="edit-submit-btn"
                >
                  {isSaving ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => editGuardedLeave(() => setIsEditModalOpen(false))}
                  className="flex-1"
                  disabled={isSaving}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      <DiscardChangesDialog
        open={editPromptOpen}
        onKeepEditing={editCancelLeave}
        onDiscard={editConfirmLeave}
      />

      {/* Invite Players Modal */}
      <Dialog open={inviteModalOpen} onOpenChange={setInviteModalOpen}>
        <DialogContent className="sm:max-w-md flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Invite Players</DialogTitle>
            <DialogDescription>
              Select players to invite to "{game?.title}"
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 flex-1 min-h-0 overflow-y-auto pb-4">
            {canUseHostTools && clubInviteResults.filter(attempt => attempt.gameId === game.id && attempt.viewerId === user?.id).map((attempt, index) => (
              <BulkInvitationResults key={index} data={attempt.data} clubName={attempt.clubName} />
            ))}
            {canUseHostTools && clubInviteResults.some(attempt => attempt.gameId === game.id && attempt.viewerId === user?.id) && (
              <p className="text-xs text-gray-600">Results stay available while this session page remains open. Reloading or leaving clears them.</p>
            )}
            {canUseHostTools && invitingClubId && (
              <p role="status" className="text-sm">Creating invitations and processing notifications. Results will appear here when this attempt finishes.</p>
            )}
            {/* Invite a Club (fan-out) — host-only block. Hidden when the host
                has zero clubs. Each club fires POST /games/{id}/invite-club after
                a confirmation step; eligibility is decided server-side. */}
            {canUseHostTools && !loadingHostClubs && hostClubs.length > 0 && (
              <div className="p-3 bg-orange-50 rounded-lg border border-orange-100" data-testid="invite-club-block">
                <p className="text-xs font-semibold text-orange-700 mb-2">Invite a club</p>
                <div className="space-y-2">
                  {hostClubs.map((club) => {
                    const isInvited = invitedClubIds.has(club.id);
                    const isInviting = invitingClubId === club.id;
                    return (
                      <div
                        key={club.id}
                        className="flex items-center justify-between gap-2 p-2 rounded-md bg-white border border-orange-100"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <Users className="w-4 h-4 text-orange-500 flex-shrink-0" />
                          <span className="text-sm font-medium text-zinc-800 truncate">{club.name}</span>
                        </div>
                        <Button
                          size="sm"
                          disabled={!!invitingClubId || isInvited}
                          onClick={() => setConfirmClub(club)}
                          className={isInvited ? 'bg-zinc-100 text-zinc-500' : 'bg-orange-500 hover:bg-orange-600 text-white'}
                          data-testid={`invite-club-option-${club.id}`}
                        >
                          {isInviting ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : isInvited ? (
                            'Invited'
                          ) : (
                            <>
                              <UserPlus className="w-3 h-3 mr-1" />
                              Invite
                            </>
                          )}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Search */}
            <Input
              placeholder="Search players..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="invite-search-input"
            />
            
            {/* Share Link */}
            <div className="p-3 bg-zinc-50 rounded-lg">
              <p className="text-xs text-zinc-500 mb-2">Or share invite link:</p>
              <div className="flex gap-2">
                <Input 
                  readOnly 
                  value={game ? `${window.location.origin}/join/${game.id}` : ''}
                  className="text-sm"
                />
                <Button 
                  size="sm" 
                  onClick={copyInviteLink}
                  data-testid="copy-invite-link-btn"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            {/* Player List */}
            <div className="max-h-64 overflow-y-auto space-y-2">
              {loadingInvitablePlayers ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
                </div>
              ) : filteredInvitablePlayers.length === 0 ? (
                <p className="text-center text-zinc-500 py-4">
                  {searchQuery ? 'No players match your search' : 'No players available to invite'}
                </p>
              ) : (
                filteredInvitablePlayers.map(player => {
                  const isIneligible = player.eligible === false;
                  const isDisabled = isIneligible || player.already_invited || invitingPlayer === player.id;
                  
                  return (
                    <div 
                      key={player.id}
                      className={`flex items-center justify-between p-2 rounded-lg ${isIneligible ? 'bg-zinc-50 opacity-60' : 'hover:bg-zinc-50'}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-medium text-sm ${
                          isIneligible ? 'bg-zinc-200 text-zinc-400' : 'bg-orange-100 text-orange-600'
                        }`}>
                          {invitationPlayerLabel(player).charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <p className={`font-medium text-sm ${isIneligible ? 'text-zinc-400' : ''}`}>
                            {invitationPlayerLabel(player)}
                          </p>
                          {/* Show ineligible reason OR skill level */}
                          {isIneligible && player.ineligible_reason ? (
                            <p className="text-xs text-red-500">{player.ineligible_reason}</p>
                          ) : player.skill_level ? (
                            <p className="text-xs text-zinc-500">{player.skill_level}</p>
                          ) : null}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        disabled={isDisabled}
                        onClick={() => handleInvitePlayer(player.id)}
                        className={
                          isIneligible
                            ? 'bg-zinc-200 text-zinc-400 cursor-not-allowed'
                            : player.already_invited 
                              ? 'bg-zinc-100 text-zinc-500' 
                              : 'bg-orange-500 hover:bg-orange-600 text-white'
                        }
                        data-testid={`invite-btn-${player.id}`}
                      >
                        {invitingPlayer === player.id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : player.already_invited ? (
                          'Invited'
                        ) : isIneligible ? (
                          'Ineligible'
                        ) : (
                          <>
                            <UserPlus className="w-3 h-3 mr-1" />
                            Invite
                          </>
                        )}
                      </Button>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Sticky Done footer — always thumb-reachable on mobile */}
          <div
            className="sticky bottom-0 left-0 right-0 -mx-4 px-4 pt-3 bg-background border-t border-zinc-200 sm:-mx-6 sm:px-6"
            style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
          >
            <Button
              onClick={() => setInviteModalOpen(false)}
              className="w-full bg-orange-500 hover:bg-orange-600 text-white h-11 font-semibold"
              data-testid="invite-done-btn"
            >
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Club fan-out confirmation — bulk action guard before inviting all
          eligible members of the selected club. */}
      <AlertDialog open={!!confirmClub} onOpenChange={(o) => { if (!o) setConfirmClub(null); }}>
        <AlertDialogContent data-testid="invite-club-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Invite {confirmClub?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              Club members who meet this session’s age and eligibility requirements will be invited.
              Members already invited or on the roster will be skipped.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmClub(null)}
              data-testid="invite-club-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleInviteClub(confirmClub)}
              className="bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="invite-club-confirm-btn"
            >
              Invite
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Follow Nudge Banner - Shows after joining a game */}
      {showFollowNudge && (
        <PostGameFollowNudge 
          gameId={game?.id}
          gameTitle={game?.title}
          onDismiss={() => setShowFollowNudge(false)}
        />
      )}
      
      {/* Share Run Sheet */}
      <ShareRunSheet 
        open={shareSheetOpen}
        onOpenChange={setShareSheetOpen}
        run={game}
        source="game_detail"
      />
      
      {/* Post-Join Share Modal */}
      <PostJoinShareModal
        open={postJoinModalOpen}
        onOpenChange={setPostJoinModalOpen}
        run={game}
      />
      
      {/* Quick Invite Drawer (Squad Suggestions) */}
      <QuickInviteDrawer
        open={quickInviteOpen}
        onOpenChange={setQuickInviteOpen}
        gameId={game?.id}
        suggestions={squadSuggestions.filter(s => !invitedSquadUsers.has(s.user_id))}
        onInviteSent={handleSquadInviteSent}
      />

      {/* Directions Picker (Maps App Selector) */}
      <DirectionsSheet
        open={directionsOpen}
        onOpenChange={setDirectionsOpen}
        game={game}
      />

      {/* Manage Run Sheet (Edit / Clone / Cancel / Admin Cancel) */}
      <ManageRunSheet
        open={manageRunOpen}
        onOpenChange={setManageRunOpen}
        noun={runNoun}
        canEdit={canUseHostTools && game?.status !== 'completed' && game?.status !== 'cancelled'}
        canClone={isCreator && game?.status !== 'completed' && game?.status !== 'cancelled'}
        canCancel={isCreator && game?.status !== 'completed' && game?.status !== 'cancelled'}
        canAdminCancel={
          !isCreator &&
          user && ['admin', 'scout'].includes(user.role) &&
          game?.status !== 'cancelled' && game?.status !== 'completed'
        }
        onEdit={handleEditClick}
        onClone={() => navigate('/create-game', { state: { cloneFrom: game } })}
        onCancel={() => setShowCancelModal(true)}
        onAdminCancel={() => setShowAdminCancelModal(true)}
      />
      
      
    </div>
  );
};

export default GameDetail;
